<template>
    <div id="page-config" data-page="config" class="config-page pt-4 pb-5 config-bottom-spacer">
        <validation-messages ref="form-validation"></validation-messages>


            <!-- Top Header -->
        <div class="d-flex flex-wrap justify-content-between align-items-center mb-4">
            <div>
                <h4 class="font-weight-bold text-dark mb-1">
                    <i class="bi bi-gear-wide-connected text-primary mr-2"></i>Configuration
                </h4>
                <div class="text-muted small">
                    Manage cloud platform connections and your connected project spaces.
                </div>
            </div>
            <div class="mt-3 mt-md-0 d-flex align-items-center">
                <b-button v-if="archivedCount > 0" variant="outline-secondary" size="sm" class="shadow-sm px-3"
                    @click="confirmPurgeArchives" title="Permanently delete archived project spaces">
                    <i class="bi bi-archive mr-1"></i>Clean Up Archives ({{ archivedCount }})
                </b-button>
            </div>
        </div>

        <!-- Section Navigation Shortcuts -->
        <div class="d-flex flex-wrap align-items-center mb-4 pb-2 border-bottom config-section-nav">
            <span class="text-muted small mr-3 font-weight-bold text-uppercase">Sections:</span>
            <b-button variant="outline-info" size="sm" class="mr-2 mb-2 px-3 shadow-sm rounded-pill font-weight-bold" @click="scrollToSection('section-connections')">
                <i class="bi bi-cloud-arrow-up mr-1"></i>1. Connections
                <span class="section-counter-badge ml-1" data-test="badge-connections-count">{{ platformLinks.length }}</span>
            </b-button>
            <b-button variant="outline-primary" size="sm" class="mr-2 mb-2 px-3 shadow-sm rounded-pill font-weight-bold" @click="scrollToSection('section-projects')">
                <i class="bi bi-kanban mr-1"></i>2. Project Spaces
                <span class="section-counter-badge ml-1" data-test="badge-projects-count">{{ projectsList.length }}</span>
            </b-button>
            <b-button variant="outline-success" size="sm" class="mb-2 px-3 shadow-sm rounded-pill font-weight-bold" data-test="btn-scroll-teams" @click="scrollToSection('section-teams')">
                <i class="bi bi-people mr-1"></i>3. Teams
                <span class="section-counter-badge ml-1" data-test="badge-teams-count">{{ teamsList.length }}</span>
            </b-button>
        </div>


        <!-- SECTION 1: CONNECTIONS -->
        <b-card no-body class="shadow-sm border mb-4 config-section-card" id="section-connections">
            <div class="card-header bg-white border-bottom py-3 px-4 d-flex flex-wrap justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                    <span class="section-step-badge mr-3">1</span>
                    <div class="section-icon mr-2 text-info">
                        <i class="bi bi-cloud-arrow-up-fill fa-lg"></i>
                    </div>
                    <div>
                        <h5 class="font-weight-bold text-dark mb-0 d-flex align-items-center">
                            Connections
                            <b-badge pill variant="info" class="ml-2 px-2 font-weight-bold">{{ platformLinks.length }}</b-badge>
                        </h5>
                        <div class="small text-muted">
                            Connect your cloud service accounts (Azure DevOps, GitHub, GitLab, Jira) to authenticate VelocyTrack.
                        </div>
                    </div>
                </div>
                <div class="mt-2 mt-md-0">
                    <b-button variant="primary" size="sm" data-test="btn-add-connection" @click="openAddPlatformLinkModal">
                        <i class="bi bi-plus-lg mr-1"></i>Add Connection
                    </b-button>
                </div>
            </div>
            <b-card-body class="p-4">

                    <b-overlay :show="loadingPlatformLinks" rounded="lg" opacity="0.6" class="config-panel-overlay">
                        <!-- Empty Links State -->
                        <div v-if="!platformLinks.length && !loadingPlatformLinks"
                            class="empty-state-card text-center py-5 px-4 rounded border my-2"
                            data-test="state-empty-connections">
                            <div class="empty-icon-circle mb-3 mx-auto">
                                <i class="bi bi-cloud-plus text-info"></i>
                            </div>
                            <h5 class="font-weight-bold text-dark mb-2">No Cloud Platforms Connected</h5>
                            <p class="text-muted small mb-0 mx-auto" style="max-width: 480px;">
                                Add a connection to Azure DevOps, GitHub, or other supported tools to link work items
                                and pull requests.
                            </p>
                        </div>

                        <!-- Platform Links Grid -->
                        <b-row v-else class="platform-cards-grid">
                            <b-col v-for="link in platformLinks" :key="link.id" lg="6" md="12" class="mb-4">
                                <div class="platform-card rounded shadow-sm h-100 d-flex flex-column border"
                                    data-test="card-connection"
                                    :data-test-id="link.id">
                                    <!-- Header -->
                                    <div
                                        class="card-header border-bottom py-3 px-3 d-flex justify-content-between align-items-center">
                                        <div class="d-flex align-items-center text-truncate mr-2">
                                            <div class="platform-type-icon mr-2"
                                                :class="'icon-' + platformIconName(link.platformType)">
                                                <i :class="platformIconClass(link.platformType)" class="fa-lg"></i>
                                            </div>
                                            <div class="text-truncate">
                                                <h6 class="font-weight-bold text-dark mb-0 text-truncate">{{ link.name
                                                }}</h6>
                                                <span class="small text-muted">{{ platformTypeName(link.platformType)
                                                }}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <span v-if="link.isVerified" class="badge badge-success px-2 py-1"
                                                v-b-tooltip.hover
                                                :title="'Last verified: ' + formatDate(link.lastVerifiedAt)">
                                                <i class="bi bi-check-circle-fill mr-1"></i>Verified
                                            </span>
                                            <span v-else class="badge badge-warning text-dark px-2 py-1">
                                                <i class="bi bi-exclamation-triangle-fill mr-1"></i>Unverified
                                            </span>
                                        </div>
                                    </div>

                                    <!-- Body -->
                                    <div class="card-body p-3 flex-grow-1">
                                        <div class="mb-2">
                                            <span class="text-muted small">Organization / Tenant:</span>
                                            <strong class="ml-1 text-dark small font-monospace">{{ link.tenantSlug
                                            }}</strong>
                                        </div>

                                        <div class="d-flex align-items-center mb-3">
                                            <span class="text-muted small mr-2">Capabilities:</span>
                                            <b-badge v-if="link.supportsBacklog" variant="primary" class="mr-1">
                                                <i class="bi bi-kanban mr-1"></i>Backlog
                                            </b-badge>
                                            <b-badge v-if="link.supportsRepos" variant="danger" class="mr-1">
                                                <i class="bi bi-git mr-1"></i>Repos & PRs
                                            </b-badge>
                                            <b-badge v-if="link.isPrimary" variant="outline-primary"
                                                class="border text-primary">
                                                Primary
                                            </b-badge>
                                        </div>

                                        <div
                                            class="mt-2 pt-2 border-top d-flex align-items-center justify-content-between extra-small text-muted">
                                            <div>
                                                <span v-if="link.hasOAuth">
                                                    <i class="icon-azure mr-1"></i>OAuth 2.0
                                                    Access
                                                </span>
                                                <span v-else>
                                                    <i class="bi bi-key-fill text-secondary mr-1"></i>Personal Access
                                                    Token (PAT)<code v-if="link.tokenHint" class="ml-1 text-muted" data-test="badge-token-hint">••••{{ link.tokenHint }}</code><a v-if="platformPatSettingsUrl(link)" :href="platformPatSettingsUrl(link)" target="_blank" rel="noopener noreferrer" class="ml-2 font-weight-bold text-primary d-inline-flex align-items-center" data-test="link-azure-pat-settings" v-b-tooltip.hover :title="platformPatSettingsTooltip(link)"><span>PAT Settings</span><i class="bi bi-box-arrow-up-right ml-1"></i></a>
                                                </span>
                                            </div>
                                            <span v-if="getLinkProjectCount(link.id) > 0"
                                                class="badge badge-light border text-muted" v-b-tooltip.hover
                                                title="Active projects linked to this connection">
                                                <i class="bi bi-kanban mr-1"></i>{{ getLinkProjectCount(link.id) }}
                                                project{{
                                                    getLinkProjectCount(link.id) > 1 ? 's' : '' }}
                                            </span>
                                        </div>
                                    </div>

                                    <!-- Footer Actions -->
                                    <div
                                        class="card-footer border-top py-2 px-3 d-flex justify-content-between align-items-center">
                                        <b-button variant="outline-primary" size="sm"
                                            :disabled="verifyingLinkId === link.id"
                                            data-test="btn-verify-connection"
                                            :data-state="verifyingLinkId === link.id ? 'loading' : 'idle'"
                                            @click="verifyPlatformLinkAsync(link)">
                                            <b-spinner v-if="verifyingLinkId === link.id" small
                                                class="mr-1"></b-spinner>
                                            <i v-else class="bi bi-arrow-repeat mr-1"></i>
                                            Verify Connection
                                        </b-button>
                                        <div>
                                            <b-button variant="outline-secondary" size="sm" class="mr-2"
                                                data-test="btn-edit-connection"
                                                @click="editPlatformLink(link)">
                                                <i class="bi bi-pencil mr-1"></i>Edit
                                            </b-button>
                                            <b-button variant="outline-danger" size="sm"
                                                data-test="btn-delete-connection"
                                                aria-label="Delete platform connection"
                                                @click="deletePlatformLinkAsync(link)"
                                                :title="getLinkProjectCount(link.id) > 0 ? 'Cannot delete: Connection is in use by projects' : 'Delete platform connection'">
                                                <i class="bi bi-trash"></i>
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </b-col>
                        </b-row>
                        <template #overlay>
                            <load-spinner text="Loading platform connections..." />
                        </template>
                    </b-overlay>
            </b-card-body>
        </b-card>

        <!-- SECTION 2: PROJECT SPACES -->
        <b-card no-body class="shadow-sm border mb-4 config-section-card" id="section-projects">
            <div class="card-header bg-white border-bottom py-3 px-4 d-flex flex-wrap justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                    <span class="section-step-badge mr-3">2</span>
                    <div class="section-icon mr-2 text-primary">
                        <i class="bi bi-kanban-fill fa-lg"></i>
                    </div>
                    <div>
                        <h5 class="font-weight-bold text-dark mb-0 d-flex align-items-center">
                            Project Spaces
                            <b-badge pill variant="primary" class="ml-2 px-2 font-weight-bold">{{ projectsList.length }}</b-badge>
                        </h5>
                        <div class="small text-muted">
                            Each Project Space pairs a backlog source (tasks/sprints) and an optional repository source (PRs/code).
                        </div>
                    </div>
                </div>
                <div class="mt-2 mt-md-0">
                    <b-button variant="primary" size="sm"
                        data-test="btn-add-project"
                        @click="openAddProjectWizard">
                        <i class="bi bi-plus-circle mr-1"></i>New Project Space
                    </b-button>
                </div>
            </div>
            <b-card-body class="p-4">
                <!-- Gate: require at least one platform connection -->
                <b-alert v-if="!platformLinks.length && !loadingPlatformLinks" show variant="warning"
                    class="d-flex align-items-center mb-4 shadow-sm">
                    <i class="bi bi-exclamation-triangle-fill text-warning mr-3" style="font-size:1.4rem;"></i>
                    <div>
                        <strong class="d-block mb-1">No Platform Connection yet</strong>
                        <div class="small">
                            You need to add at least one Platform Connection before you can set up project spaces.
                            <a href="#section-connections" class="alert-link font-weight-bold"
                                @click.prevent="scrollToSection('section-connections')">Add a connection in Section 1 (Connections)</a> to get started.
                        </div>
                    </div>
                </b-alert>

                    <b-overlay :show="loadingProjects" rounded="lg" opacity="0.6" class="config-panel-overlay">
                        <!-- Empty state: connections exist but no projects yet -->
                        <div v-if="!projectsList.length && !loadingProjects && platformLinks.length"
                            class="empty-state-card text-center py-5 px-4 rounded border my-2">
                            <div class="empty-icon-circle mb-3 mx-auto">
                                <i class="bi bi-folder-plus text-primary"></i>
                            </div>
                            <h5 class="font-weight-bold text-dark mb-2">No Project Spaces Configured Yet</h5>
                            <p class="text-muted small mb-0 mx-auto" style="max-width: 480px;">
                                Set up a project space to start logging time against backlog tasks and tracking pull requests.
                            </p>
                        </div>

                        <!-- Projects Cards Grid -->
                        <b-row v-if="projectsList.length" class="project-cards-grid">
                            <b-col v-for="proj in projectsList" :key="proj.id" lg="6" md="12" class="mb-4">
                                <div class="project-card rounded shadow-sm h-100 d-flex flex-column border"
                                    :class="{ 'is-primary-card': proj.isPrimary }"
                                    data-test="card-project"
                                    :data-test-id="proj.id">
                                    <!-- Card Header -->
                                    <div
                                        class="card-header border-bottom py-3 px-3 d-flex justify-content-between align-items-center">
                                        <div class="d-flex align-items-center text-truncate mr-2">
                                            <div class="project-avatar-icon mr-2"
                                                :class="proj.isPrimary ? 'bg-primary text-white' : 'bg-light text-secondary'">
                                                <i class="bi bi-folder-fill"></i>
                                            </div>
                                            <div class="text-truncate">
                                                <h6 class="font-weight-bold text-dark mb-0 text-truncate"
                                                    :title="proj.projectName">
                                                    {{ proj.projectName }}
                                                </h6>
                                                <div class="d-flex flex-wrap align-items-center mt-1">
                                                    <span v-if="proj.isPrimary"
                                                        class="badge badge-primary font-weight-bold mr-1">
                                                        <i class="bi bi-star-fill mr-1"></i>Primary Project Space
                                                    </span>
                                                    <span v-if="proj.isSharedProjectSpace || proj.sharedWorkspaceId"
                                                        class="badge badge-info font-weight-bold"
                                                        data-test="badge-shared-team">
                                                        <i class="bi bi-people-fill mr-1"></i>Team: {{ proj.sharedWorkspaceName || 'Shared Squad' }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="d-flex align-items-center">
                                            <b-badge class="capacity-badge mr-2">
                                                {{ proj.dailyCapacity || 8 }}h / day
                                            </b-badge>
                                        </div>
                                    </div>

                                    <!-- Card Body: Backlog & Repo Sources -->
                                    <div class="card-body p-3 flex-grow-1">
                                        <!-- Shared Project Space Personal Auth Callout -->
                                        <div v-if="proj.requiresPersonalAuth"
                                            class="alert alert-warning border small py-2 px-3 mb-3 d-flex align-items-center justify-content-between"
                                            data-test="alert-connect-shared-space-auth">
                                            <div class="mr-2">
                                                <strong class="d-block"><i class="bi bi-key-fill mr-1"></i>Personal Cloud Auth Needed</strong>
                                                <span class="extra-small">
                                                    Connect your personal <strong>{{ platformTypeName(proj.requiredBacklogPlatformType ?? proj.backlogPlatformType) }}</strong>
                                                    (<code class="font-weight-bold">{{ proj.requiredBacklogTenantSlug || proj.requiredRepoTenantSlug || 'org' }}</code>) account to sync tasks &amp; PRs.
                                                </span>
                                            </div>
                                            <b-button variant="warning" size="sm" class="font-weight-bold text-nowrap"
                                                data-test="btn-connect-shared-space-auth"
                                                @click="connectSharedProjectSpaceAuth(proj)">
                                                <i class="bi bi-plug-fill mr-1"></i>Connect Account
                                            </b-button>
                                        </div>

                                        <!-- Backlog Source Row -->
                                        <div class="source-row mb-3 p-2 rounded border">
                                            <div class="d-flex align-items-center justify-content-between mb-1">
                                                <span class="small font-weight-bold text-secondary">
                                                    <i class="bi bi-kanban text-primary mr-1"></i>Backlog &amp; Sprints
                                                </span>
                                                <b-badge v-if="proj.backlogLinkName"
                                                    :variant="platformBadgeVariant(proj.backlogPlatformType)"
                                                    class="px-2 py-1">
                                                    <i :class="platformIconOnly(proj.backlogPlatformType)"
                                                        class="mr-1"></i>{{
                                                            proj.backlogLinkName }}
                                                </b-badge>
                                                <span v-else class="badge badge-light border text-muted">None</span>
                                            </div>
                                            <div v-if="proj.backlogProjectName"
                                                class="small text-dark font-weight-500 text-truncate">
                                                <i class="bi bi-folder2-open mr-1 text-muted"></i>{{
                                                    proj.backlogProjectName }}
                                                <span v-if="proj.backlogTeamName" class="text-muted font-weight-normal">
                                                    &nbsp;&bull;&nbsp;<i class="bi bi-people mr-1"></i>{{
                                                        proj.backlogTeamName }}
                                                </span>
                                            </div>
                                            <div v-else class="extra-small text-muted italic">
                                                No backlog source attached
                                            </div>
                                        </div>

                                        <!-- Repo Source Row -->
                                        <div class="source-row p-2 rounded border mb-2">
                                            <div class="d-flex align-items-center justify-content-between mb-1">
                                                <span class="small font-weight-bold text-secondary">
                                                    <i class="bi bi-git text-danger mr-1"></i>Repositories &amp; PRs
                                                </span>
                                                <b-badge v-if="proj.repoLinkName"
                                                    :variant="platformBadgeVariant(proj.repoPlatformType)"
                                                    class="px-2 py-1">
                                                    <i :class="platformIconOnly(proj.repoPlatformType)"
                                                        class="mr-1"></i>{{
                                                            proj.repoLinkName }}
                                                </b-badge>
                                                <span v-else class="badge badge-light border text-muted">None</span>
                                            </div>
                                            <div v-if="proj.repoProjectName"
                                                class="small text-dark font-weight-500 text-truncate">
                                                <i class="bi bi-code-slash mr-1 text-muted"></i>{{ proj.repoProjectName }}
                                            </div>
                                            <div v-else class="extra-small text-muted italic">
                                                No repository source attached
                                            </div>
                                        </div>

                                        <!-- Role & Details -->
                                        <div class="d-flex align-items-center mt-2 extra-small text-muted">
                                            <span class="mr-3">
                                                <i class="bi bi-person-badge mr-1"></i>Role:
                                                <strong data-test="project-card-role">{{ roleName(proj.employeeRoleOverride ?? currentUserDefaultRole
                                                    ?? 0)
                                                }}</strong>
                                            </span>
                                        </div>
                                    </div>

                                    <!-- Card Footer Actions -->
                                    <div
                                        class="card-footer border-top py-2 px-3 d-flex justify-content-between align-items-center">
                                        <div>
                                            <b-button v-if="!proj.isPrimary" variant="link" size="sm"
                                                class="p-0 text-primary font-weight-500 mr-3"
                                                data-test="btn-set-primary-project"
                                                @click="setPrimaryProjectAsync(proj)">
                                                <i class="bi bi-star mr-1"></i>Set as Primary Space
                                            </b-button>
                                        </div>
                                        <div v-if="proj.isTeamMemberSpace || proj.canManageSpace === false" class="d-flex align-items-center">
                                            <b-badge variant="light" class="border text-muted py-1 px-2 mr-2" data-test="badge-managed-by-team">
                                                <i class="bi bi-lock-fill mr-1 text-secondary"></i>Managed by Team — Auth Only
                                            </b-badge>
                                            <b-button variant="outline-secondary" size="sm" class="mr-2"
                                                data-test="btn-edit-member-role"
                                                @click="openEditProjectModal(proj, { memberPreferencesOnly: true })" title="Edit my role and daily capacity for this project space">
                                                <i class="bi bi-person-badge mr-1"></i>Role & Capacity
                                            </b-button>
                                            <b-button variant="outline-warning" size="sm"
                                                data-test="btn-connect-shared-space-auth"
                                                @click="connectSharedProjectSpaceAuth(proj)" title="Connect / Update personal cloud authentication">
                                                <i class="bi bi-plug-fill mr-1"></i>Connect / Update Auth
                                            </b-button>
                                        </div>
                                        <div v-else class="d-flex align-items-center">
                                            <b-button variant="outline-secondary" size="sm" class="mr-2"
                                                data-test="btn-edit-project"
                                                @click="openEditProjectModal(proj)" title="Edit project space settings">
                                                <i class="bi bi-pencil mr-1"></i>Edit
                                            </b-button>
                                            <b-button variant="outline-danger" size="sm"
                                                data-test="btn-delete-project"
                                                aria-label="Archive or delete project space"
                                                @click="deleteProjectAsync(proj)" title="Archive or delete project space">
                                                <i class="bi bi-trash"></i>
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </b-col>
                        </b-row>
                        <template #overlay>
                            <load-spinner text="Loading project spaces..." />
                        </template>
                    </b-overlay>
            </b-card-body>
        </b-card>

        <!-- SECTION 3: TEAMS & SQUAD COLLABORATION -->
        <b-card no-body class="shadow-sm border mb-4 config-section-card" id="section-teams" data-test="section-teams">
            <div class="card-header bg-white border-bottom py-3 px-4 d-flex flex-wrap justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                    <span class="section-step-badge mr-3">3</span>
                    <div class="section-icon mr-2 text-success">
                        <i class="bi bi-people-fill fa-lg"></i>
                    </div>
                    <div>
                        <h5 class="font-weight-bold text-dark mb-0 d-flex align-items-center">
                            {{ isDevEnv ? 'Team Spaces & Squads' : 'Teams' }}
                            <b-badge v-if="!isDevEnv" pill variant="info" class="ml-2 px-2 font-weight-bold"><i class="bi bi-clock-history mr-1"></i>Coming Next</b-badge>
                            <b-badge v-else pill variant="success" class="ml-2 px-2 font-weight-bold">{{ teamsList.length }}</b-badge>
                        </h5>
                        <div class="small text-muted">
                            <template v-if="!isDevEnv">
                                Group projects into shared team spaces for multi-developer collaboration.
                            </template>
                            <template v-else>
                                Configure shared Team Spaces using your purchased 5-seat batches (€20/mo base + €30/mo per 5-seat batch). Team Spaces are created on demand.
                            </template>
                        </div>
                    </div>
                </div>
                <div v-if="isDevEnv" class="mt-2 mt-md-0 d-flex align-items-center">
                    <b-button v-if="!isBetaUser" variant="outline-info" size="sm" class="mr-2" data-test="btn-buy-seat-batches" @click="$router.push('/plans')">
                        <i class="fa-solid fa-crown mr-1"></i>{{ purchasedSeatSlots > 0 ? 'Add Seat Batches (+€30/mo)' : 'Upgrade to Team Plan' }}
                    </b-button>
                    <b-button variant="success" size="sm" data-test="btn-create-team"
                        :disabled="isBetaUser && !canCreateNewTeamSpace"
                        :title="isBetaUser && !canCreateNewTeamSpace ? 'Beta accounts include 1 batch (5 seats / 1 Team Space). Move to Production for additional batches.' : 'Create a new Team Space'"
                        @click="openCreateTeamModal">
                        <i class="bi bi-plus-circle mr-1"></i>Create Team Space
                    </b-button>
                </div>
            </div>
            <b-card-body class="p-4">
                <div v-if="!isDevEnv" class="empty-state-card text-center py-5 px-4 rounded border my-2">
                    <div class="empty-icon-circle mb-3 mx-auto">
                        <i class="bi bi-people text-success"></i>
                    </div>
                    <h5 class="font-weight-bold text-dark mb-2">Team Workspaces — Coming Next</h5>
                    <p class="text-muted small mb-0 mx-auto" style="max-width: 500px;">
                        Shared team workspaces for collaborative sprint tracking and peer velocity reporting
                        are coming next. Stay tuned!
                    </p>
                </div>
                <template v-else>
                <!-- Purchased 5-Seat Batch Pool & Beta Limit Summary Bar -->
                <div class="p-3 rounded border source-row mb-4 d-flex flex-wrap justify-content-between align-items-center" data-test="bar-team-batch-pool">
                    <div class="mb-2 mb-md-0">
                        <div class="d-flex flex-wrap align-items-center">
                            <span class="font-weight-bold text-dark small mr-2">
                                <i class="bi bi-layers-fill text-info mr-1"></i>5-Seat Batch Pool:
                            </span>
                            <b-badge variant="info" class="mr-2 px-2 py-1" data-test="badge-purchased-batches">
                                {{ purchasedSeatSlots }} Purchased {{ purchasedSeatSlots === 1 ? 'Batch' : 'Batches' }} ({{ purchasedSeatSlots * 5 }} Seats)
                            </b-badge>
                            <b-badge variant="secondary" class="mr-2 px-2 py-1" data-test="badge-allocated-batches">
                                {{ allocatedSeatSlots }} Allocated
                            </b-badge>
                            <b-badge :variant="availableSeatSlots > 0 ? 'success' : 'warning'" class="px-2 py-1" data-test="badge-available-batches">
                                {{ availableSeatSlots }} Unallocated {{ availableSeatSlots === 1 ? 'Batch' : 'Batches' }} Available ({{ availableSeatSlots * 5 }} Seats)
                            </b-badge>
                        </div>
                        <div v-if="isBetaUser" class="extra-small text-muted mt-1" data-test="notice-beta-batch-limit">
                            <i class="fa-solid fa-flask text-warning mr-1"></i>
                            <strong>Private Beta Limit:</strong> Beta accounts include <strong>1 complimentary 5-seat batch</strong> (1 Team Space with up to 5 seats). Need additional 5-seat batches or multiple Team Spaces? Please move to the Production instance (<strong>velocytrack.com</strong>).
                        </div>
                        <div v-else-if="purchasedSeatSlots === 0" class="extra-small text-muted mt-1">
                            Upgrade to the <strong>Team Plan (€20/mo + €30/mo per 5-seat batch)</strong> to unlock Team Spaces and allocate 5-seat batches across your squads.
                        </div>
                        <div v-else class="extra-small text-muted mt-1">
                            Each Team Space uses at least 1 batch slot (5 seats). You can distribute your purchased batches across one or multiple Team Spaces.
                        </div>
                    </div>
                </div>

                <!-- Incoming Pending Team Invitations Banner -->
                <div v-if="incomingInvitations.length" class="mb-4" data-test="banner-team-invitations">
                    <div v-for="inv in incomingInvitations" :key="inv.invitationId || inv.token"
                        class="alert alert-info border shadow-sm d-flex flex-wrap justify-content-between align-items-center mb-2 py-3 px-3"
                        data-test="item-team-invitation">
                        <div class="mr-3 mb-2 mb-md-0">
                            <div class="font-weight-bold text-dark">
                                <i class="bi bi-envelope-paper-heart-fill text-info mr-2"></i>
                                You've been invited to join <strong>{{ inv.workspaceName }}</strong>
                                <b-badge variant="info" class="ml-2">{{ wsRoleName(inv.role) }}</b-badge>
                            </div>
                            <div class="small text-muted mt-1">
                                Invited by <strong>{{ inv.invitedByName || inv.invitedByEmail || 'Team Lead' }}</strong>
                                <span v-if="inv.sharedProjectSpaceNames && inv.sharedProjectSpaceNames.length">
                                    &bull; Shared Project Spaces: <strong>{{ inv.sharedProjectSpaceNames.join(', ') }}</strong>
                                </span>
                            </div>
                        </div>
                        <div class="d-flex align-items-center">
                            <b-button variant="success" size="sm" class="mr-2 font-weight-bold"
                                :disabled="respondingInviteToken === inv.token"
                                data-test="btn-accept-team-invite"
                                @click="acceptTeamInvitationAsync(inv)">
                                <b-spinner small v-if="respondingInviteToken === inv.token" class="mr-1"></b-spinner>
                                <i v-else class="bi bi-check-lg mr-1"></i>Accept &amp; Join Squad
                            </b-button>
                            <b-button variant="outline-secondary" size="sm"
                                :disabled="respondingInviteToken === inv.token"
                                data-test="btn-decline-team-invite"
                                @click="declineTeamInvitationAsync(inv)">
                                Decline
                            </b-button>
                        </div>
                    </div>
                </div>

                <b-overlay :show="wsLoading" rounded="lg" opacity="0.6" class="config-panel-overlay">
                    <!-- Empty Teams State -->
                    <div v-if="!teamsList.length && !wsLoading"
                        class="empty-state-card text-center py-5 px-4 rounded border my-2"
                        data-test="state-empty-teams">
                        <div class="empty-icon-circle mb-3 mx-auto">
                            <i class="bi bi-people text-success"></i>
                        </div>
                        <h5 class="font-weight-bold text-dark mb-2">No Team Spaces Configured Yet</h5>
                        <p class="text-muted small mb-3 mx-auto" style="max-width: 560px;">
                            Team Spaces are not created automatically. Use your available 5-seat batch slot(s) to create a Team Space, link shared Project Spaces, and invite your squad—while keeping your personal Project Spaces 100% private.
                        </p>
                        <b-button v-if="canCreateNewTeamSpace" variant="success" size="sm" class="px-3 font-weight-bold" data-test="btn-empty-create-team" @click="openCreateTeamModal">
                            <i class="bi bi-plus-circle mr-1"></i>Create Your First Team Space ({{ availableSeatSlots }} {{ availableSeatSlots === 1 ? 'Batch' : 'Batches' }} Available)
                        </b-button>
                        <b-button v-else-if="!isBetaUser" variant="info" size="sm" class="px-3 font-weight-bold" data-test="btn-empty-upgrade-team" @click="$router.push('/plans')">
                            <i class="fa-solid fa-crown mr-1"></i>Upgrade to Team Plan (€20 + €30/batch)
                        </b-button>
                    </div>

                    <!-- Teams Grid -->
                    <b-row v-else class="project-cards-grid">
                        <b-col v-for="team in teamsList" :key="team.workspaceId" lg="6" md="12" class="mb-4">
                            <div class="project-card rounded shadow-sm h-100 d-flex flex-column border"
                                data-test="card-team"
                                :data-test-id="team.workspaceId">
                                <!-- Team Card Header -->
                                <div class="card-header border-bottom py-3 px-3 d-flex justify-content-between align-items-center">
                                    <div class="d-flex align-items-center text-truncate mr-2">
                                        <div class="project-avatar-icon mr-2 bg-success text-white">
                                            <i class="bi bi-people-fill"></i>
                                        </div>
                                        <div class="text-truncate">
                                            <h6 class="font-weight-bold text-dark mb-0 text-truncate" :title="team.name">
                                                {{ team.name }}
                                            </h6>
                                            <div class="d-flex flex-wrap align-items-center mt-1">
                                                <b-badge :variant="isTeamOwnerOrAdmin(team) ? 'primary' : 'secondary'" class="mr-1" data-test="badge-team-role">
                                                    {{ wsRoleName(team.currentUserRole) }}
                                                </b-badge>
                                                <span class="extra-small text-muted">
                                                    {{ teamSlotCount(team) }} {{ teamSlotCount(team) === 1 ? 'Slot' : 'Slots' }} ({{ team.seatLimit || 5 }} Seats)
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="text-right">
                                        <b-badge variant="info" class="px-2 py-1" data-test="badge-team-seats">
                                            <i class="bi bi-person-check-fill mr-1"></i>{{ team.activeMembersCount ?? 0 }} / {{ team.seatLimit || 5 }} Seats
                                        </b-badge>
                                    </div>
                                </div>

                                <!-- Team Card Body -->
                                <div class="card-body p-3 flex-grow-1">
                                    <p class="small text-muted mb-3">
                                        {{ team.description || 'Shared engineering squad workspace for sprint deliverables, capacity balancing, and absence scheduling.' }}
                                    </p>

                                    <!-- 5-Seat Batch Utilization Bar -->
                                    <div class="source-row p-2 rounded border mb-3" data-test="team-seat-meter">
                                        <div class="d-flex justify-content-between align-items-center extra-small mb-1">
                                            <span class="font-weight-bold text-secondary">
                                                <i class="bi bi-layers-fill text-info mr-1"></i>5-Seat Batch Capacity
                                            </span>
                                            <span class="font-weight-bold text-dark">
                                                {{ team.activeMembersCount ?? 0 }} of {{ team.seatLimit || 5 }} seats used ({{ teamSlotCount(team) }} &times; 5-seat slot)
                                            </span>
                                        </div>
                                        <b-progress :max="Math.max(5, team.seatLimit || 5)" height="6px" class="mb-1">
                                            <b-progress-bar :value="team.activeMembersCount ?? 0" variant="info"></b-progress-bar>
                                        </b-progress>
                                    </div>

                                    <!-- Linked Shared Project Spaces -->
                                    <div class="source-row p-2 rounded border">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <span class="small font-weight-bold text-secondary">
                                                <i class="bi bi-folder-symlink text-primary mr-1"></i>Shared Project Spaces ({{ (team.linkedProjectSpaces || team.sharedProjectSpaces || []).length }})
                                            </span>
                                            <b-button v-if="isTeamOwnerOrAdmin(team)" variant="link" size="sm" class="p-0 extra-small"
                                                data-test="btn-edit-team-spaces"
                                                @click="openEditTeamModal(team)">
                                                <i class="bi bi-pencil mr-1"></i>Configure
                                            </b-button>
                                        </div>
                                        <div v-if="(team.linkedProjectSpaces || team.sharedProjectSpaces || []).length" class="d-flex flex-wrap mt-1">
                                            <span v-for="sp in (team.linkedProjectSpaces || team.sharedProjectSpaces)" :key="sp.id || sp.projectSpaceName"
                                                class="badge badge-light border text-dark mr-1 mb-1 px-2 py-1 d-inline-flex align-items-center"
                                                data-test="badge-team-shared-space">
                                                <i :class="platformIconOnly(sp.backlogPlatformType)" class="mr-1"></i>
                                                {{ sp.projectSpaceName }}
                                                <i v-if="sp.currentUserHasAuth || sp.isCurrentUserAuthConnected" class="bi bi-check-circle-fill text-success ml-1" title="Your personal cloud account is connected"></i>
                                                <i v-else class="bi bi-exclamation-circle-fill text-warning ml-1" title="Connect your personal cloud account in Section 1"></i>
                                            </span>
                                        </div>
                                        <div v-else class="extra-small text-muted italic">
                                            No shared Project Spaces linked yet.
                                        </div>
                                    </div>
                                </div>

                                <!-- Team Card Footer -->
                                <div class="card-footer border-top py-2 px-3 d-flex justify-content-between align-items-center">
                                    <b-button variant="outline-primary" size="sm"
                                        data-test="btn-open-team-dashboard"
                                        @click="openTeamDashboard(team)">
                                        <i class="bi bi-speedometer2 mr-1"></i>Team Dashboard
                                    </b-button>
                                    <div class="d-flex align-items-center">
                                        <b-button variant="primary" size="sm" class="mr-2"
                                            data-test="btn-manage-team"
                                            @click="openManageTeamModal(team)">
                                            <i class="bi bi-people-fill mr-1"></i>{{ isTeamOwnerOrAdmin(team) ? 'Manage Squad' : 'Squad Roster' }}
                                        </b-button>
                                        <b-button v-if="!isTeamOwner(team)" variant="outline-danger" size="sm"
                                            data-test="btn-leave-team"
                                            title="Leave Team"
                                            aria-label="Leave Team"
                                            @click="leaveTeamAsync(team)">
                                            <i class="bi bi-box-arrow-right"></i>
                                        </b-button>
                                        <b-button v-else variant="outline-danger" size="sm"
                                            data-test="btn-delete-team"
                                            title="Archive Team Space"
                                            aria-label="Archive Team Space"
                                            @click="deleteTeamAsync(team)">
                                            <i class="bi bi-trash"></i>
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                    <template #overlay>
                        <load-spinner text="Loading teams..." />
                    </template>
                </b-overlay>
                </template>
            </b-card-body>
        </b-card>

        <!-- ==================== CREATE / EDIT TEAM MODAL ==================== -->
        <b-modal v-model="showCreateTeamModal" :title="isEditingTeam ? 'Edit Team Space & Shared Project Spaces' : 'Create New Team Space'"
            centered hide-footer size="lg" data-test="modal-create-team">
            <b-form @submit.prevent="saveTeamAsync">
                <b-form-group label="Team Space / Squad Name" label-for="input-team-name"
                    description="E.g. Core Platform Squad, Payments Engineering, or Mobile Chapter.">
                    <b-form-input id="input-team-name" v-model.trim="teamForm.name" required maxlength="200"
                        placeholder="Enter team name..." data-test="input-team-name"></b-form-input>
                </b-form-group>

                <b-form-group label="Description (Optional)" label-for="input-team-description">
                    <b-form-textarea id="input-team-description" v-model.trim="teamForm.description" rows="2" maxlength="500"
                        placeholder="What sprints, services, or deliverables does this squad collaborate on?"
                        data-test="input-team-description"></b-form-textarea>
                </b-form-group>

                <b-form-group v-if="!isEditingTeam" label="Allocate 5-Seat Batch Slots (From Your Purchased Pool)" label-for="select-create-team-slots"
                    :description="isBetaUser ? 'Beta accounts include 1 batch slot (5 seats). Move to Production (velocytrack.com) to add more batches.' : `You have ${availableSeatSlots} unallocated batch slot(s) (${availableSeatSlots * 5} seats) available to allocate.`">
                    <b-form-select id="select-create-team-slots" v-model="teamForm.seatSlots" :options="availableTeamSlotOptions"
                        :disabled="isBetaUser || availableTeamSlotOptions.length <= 1"
                        data-test="select-create-team-slots"></b-form-select>
                </b-form-group>

                <b-form-group label="Shared Project Spaces"
                    description="Invited squad members will automatically receive these Project Space definitions (and authenticate with their own personal cloud credentials). Your unshared Project Spaces remain 100% private.">
                    <div v-if="!personalOrAllProjectsForSharing.length" class="alert alert-light border small mb-0">
                        <i class="bi bi-info-circle mr-1"></i>You haven't configured any Project Spaces in Section 2 yet. You can create the Team Space now and link Project Spaces later.
                    </div>
                    <div v-else class="p-2 rounded border source-row" style="max-height: 220px; overflow-y: auto;">
                        <b-form-checkbox v-for="proj in personalOrAllProjectsForSharing" :key="proj.id"
                            v-model="teamForm.projectSpaceIds" :value="proj.id" class="mb-2"
                            data-test="chk-team-project-space">
                            <span class="font-weight-bold text-dark small">{{ proj.projectName }}</span>
                            <span v-if="proj.backlogProjectName" class="extra-small text-muted ml-1">
                                ({{ proj.backlogProjectName }}<span v-if="proj.backlogTeamName"> &bull; {{ proj.backlogTeamName }}</span>)
                            </span>
                        </b-form-checkbox>
                    </div>
                </b-form-group>

                <div class="d-flex justify-content-end pt-3 border-top">
                    <b-button variant="secondary" class="mr-2" @click="showCreateTeamModal = false" data-test="btn-cancel-team">Cancel</b-button>
                    <b-button type="submit" variant="success" :disabled="savingTeam || !teamForm.name" data-test="btn-save-team" @click.prevent="saveTeamAsync">
                        <b-spinner small v-if="savingTeam" class="mr-1"></b-spinner>
                        <i v-else class="bi bi-check2-circle mr-1"></i>{{ isEditingTeam ? 'Save Team Changes' : 'Create Team Space' }}
                    </b-button>
                </div>
            </b-form>
        </b-modal>

        <!-- ==================== MANAGE TEAM ROSTER, INVITES & SEATS MODAL ==================== -->
        <b-modal v-model="showManageTeamModal" :title="managingTeam ? `Manage Squad — ${managingTeam.name}` : 'Manage Squad'"
            centered hide-footer size="xl" data-test="modal-manage-team">
            <div v-if="managingTeam">
                <!-- Top Summary & 5-Seat Slot Bar -->
                <div class="p-3 rounded border source-row mb-4 d-flex flex-wrap justify-content-between align-items-center">
                    <div class="mb-2 mb-md-0">
                        <div class="d-flex align-items-center">
                            <h6 class="font-weight-bold text-dark mb-0 mr-2">{{ managingTeam.name }}</h6>
                            <b-badge :variant="isSelectedTeamQuotaFull ? 'warning' : 'info'" class="mr-2" data-test="badge-team-seat-usage">
                                {{ (wsMembers && wsMembers.filter(m => m.isActive !== false && m.role !== 0 && m.role !== 'Owner').length) || (managingTeam.activeMembersCount ?? 0) }} / {{ managingTeam.seatLimit || 5 }} Seats Active
                            </b-badge>
                            <b-badge variant="secondary">
                                {{ teamSlotCount(managingTeam) }} &times; 5-Seat Batch {{ teamSlotCount(managingTeam) === 1 ? 'Slot' : 'Slots' }}
                            </b-badge>
                        </div>
                        <div class="extra-small text-muted mt-1">
                            Owner's subscription covers all invited seats in this Team Space.
                            <span v-if="isBetaUser" class="text-warning font-weight-bold ml-1">
                                (Beta Limit: 1 Batch / 5 Seats &bull; Move to Production for more)
                            </span>
                        </div>
                    </div>
                    <div class="d-flex align-items-center">
                        <b-button v-if="isTeamOwnerOrAdmin(managingTeam)" variant="outline-primary" size="sm" class="mr-2"
                            data-test="btn-edit-team-settings"
                            @click="openEditTeamModal(managingTeam)">
                            <i class="bi bi-folder-symlink mr-1"></i>Shared Spaces ({{ (managingTeam.linkedProjectSpaces || managingTeam.sharedProjectSpaces || []).length }})
                        </b-button>
                        <b-button v-if="isTeamOwner(managingTeam) && isBetaUser" variant="outline-secondary" size="sm" disabled
                            data-test="btn-add-seat-slot"
                            title="Beta accounts are limited to 1 batch (5 seats). Move to Production (velocytrack.com) to add more batches.">
                            <i class="fa-solid fa-lock mr-1"></i>1 Batch Max (Beta)
                        </b-button>
                        <b-button v-else-if="isTeamOwner(managingTeam) && availableSeatSlots > 0" variant="info" size="sm"
                            :disabled="addingSeatSlot"
                            data-test="btn-add-seat-slot"
                            @click="addFiveSeatSlotAsync(managingTeam)">
                            <b-spinner small v-if="addingSeatSlot" class="mr-1"></b-spinner>
                            <i v-else class="bi bi-plus-circle mr-1"></i>Allocate +5 Seats Batch ({{ availableSeatSlots }} left)
                        </b-button>
                        <b-button v-else-if="isTeamOwner(managingTeam)" variant="outline-info" size="sm"
                            data-test="btn-buy-seat-slot"
                            @click="showManageTeamModal = false; $router.push('/plans')">
                            <i class="fa-solid fa-crown mr-1"></i>Buy +5-Seat Batch (+€30/mo)
                        </b-button>
                    </div>
                </div>

                <!-- Seat Quota Full Alert Banner -->
                <b-alert v-if="isSelectedTeamQuotaFull" show variant="warning" class="d-flex align-items-center mb-3" data-test="alert-team-quota-full">
                    <i class="bi bi-exclamation-triangle-fill mr-2 fa-lg"></i>
                    <div>
                        <strong>Seat Quota Full:</strong> All allocated seats ({{ managingTeam.seatLimit || 5 }} seats, including pending invitations) are currently occupied. To invite more members, allocate another 5-seat batch or revoke a pending invitation below.
                    </div>
                </b-alert>

                <!-- Invite Member & Shareable Link (Owner / Admin Only) -->
                <div v-if="isTeamOwnerOrAdmin(managingTeam)" class="row mb-4">
                    <!-- Direct Email Invitation -->
                    <div class="col-lg-7 mb-3 mb-lg-0">
                        <div class="p-3 rounded border h-100">
                            <h6 class="font-weight-bold text-dark small text-uppercase mb-2">
                                <i class="bi bi-person-plus-fill text-success mr-1"></i>Invite Member by Email
                            </h6>
                            <b-form @submit.prevent="wsInviteMemberAsync">
                                <b-form-row>
                                    <b-col md="4" class="mb-2">
                                        <b-form-input v-model.trim="wsInviteEmail" type="email" size="sm" required
                                            placeholder="teammate@company.com"
                                            :disabled="isSelectedTeamQuotaFull || wsInviting"
                                            data-test="input-invite-email"></b-form-input>
                                    </b-col>
                                    <b-col md="3" class="mb-2">
                                        <b-form-select v-model="wsInviteRole" :options="wsRoleOptions" size="sm"
                                            :disabled="isSelectedTeamQuotaFull || wsInviting"
                                            data-test="select-invite-role"></b-form-select>
                                    </b-col>
                                    <b-col md="3" class="mb-2">
                                        <b-input-group size="sm" append="h/d">
                                            <b-form-input v-model.number="wsInviteCapacity" type="number" min="1" max="16"
                                                placeholder="8" title="Daily capacity hours in this team"
                                                :disabled="isSelectedTeamQuotaFull || wsInviting"
                                                data-test="input-invite-capacity"></b-form-input>
                                        </b-input-group>
                                    </b-col>
                                    <b-col md="2" class="mb-2">
                                        <b-button type="submit" variant="success" size="sm" block :disabled="wsInviting || !wsInviteEmail || isSelectedTeamQuotaFull"
                                            data-test="btn-send-invite" @click.prevent="wsInviteMemberAsync">
                                            <b-spinner small v-if="wsInviting"></b-spinner>
                                            <span v-else>Invite</span>
                                        </b-button>
                                    </b-col>
                                </b-form-row>
                            </b-form>
                            <div class="extra-small text-muted">
                                Sends an email &amp; in-app invitation and provisions shared Project Spaces upon acceptance.
                            </div>
                        </div>
                    </div>

                    <!-- Shareable Team Invite Link -->
                    <div class="col-lg-5">
                        <div class="p-3 rounded border h-100 d-flex flex-column justify-content-between">
                            <div>
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <h6 class="font-weight-bold text-dark small text-uppercase mb-0">
                                        <i class="bi bi-link-45deg text-info mr-1"></i>Shareable Squad Link
                                    </h6>
                                    <b-form-checkbox v-model="managingTeam.inviteLinkEnabled" switch size="sm"
                                        data-test="switch-invite-link"
                                        @change="toggleInviteLinkAsync(managingTeam, $event)">
                                        <span class="extra-small">{{ managingTeam.inviteLinkEnabled ? 'Enabled' : 'Disabled' }}</span>
                                    </b-form-checkbox>
                                </div>
                                <b-input-group size="sm" v-if="managingTeam.inviteLinkEnabled && managingTeam.inviteToken" class="mb-2">
                                    <b-form-input readonly :value="buildShareableInviteUrl(managingTeam.inviteToken)"
                                        class="font-monospace extra-small" data-test="input-shareable-invite-link"></b-form-input>
                                    <b-input-group-append>
                                        <b-button variant="outline-primary" @click="copyShareableInviteLink(managingTeam.inviteToken)"
                                            title="Copy Invite Link" data-test="btn-copy-invite-link">
                                            <i class="bi bi-clipboard"></i> Copy
                                        </b-button>
                                        <b-button variant="outline-secondary" @click="regenerateInviteLinkAsync(managingTeam)"
                                            title="Regenerate Link (invalidates old link)" data-test="btn-regenerate-invite-link">
                                            <i class="bi bi-arrow-clockwise"></i>
                                        </b-button>
                                    </b-input-group-append>
                                </b-input-group>
                                <div v-else class="extra-small text-muted italic mb-2">
                                    Shareable invite link is currently disabled.
                                </div>
                            </div>
                            <div class="extra-small text-muted">
                                Anyone with this link can join up to your 5-seat batch limit ({{ managingTeam.seatLimit || 5 }} seats).
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Active Squad Members Roster Table -->
                <h6 class="font-weight-bold text-dark small text-uppercase mb-2">
                    <i class="bi bi-people mr-1"></i>Squad Roster ({{ wsMembers.length }})
                </h6>
                <div class="mb-4" data-test="table-team-members">
                    <grid-shell :columns="rosterGridColumns" :empty="!wsMembers.length" :can-reset="false" empty-title="No Squad Members" empty-message="Invite team members to join this squad.">
                        <b-card v-for="m in wsMembers" :key="m.accountUid" class="my-1 py-1 shadow-none border">
                            <b-row class="my-1 grid-row align-items-center" data-test="row-team-member" :data-test-id="m.accountUid">
                                <b-col class="px-2 grid-col-member text-left">
                                    <div class="font-weight-bold text-dark small">
                                        {{ m.fullName || m.email }}
                                        <b-badge v-if="!m.isActive" variant="secondary" class="ml-1">Former Member</b-badge>
                                    </div>
                                    <div class="extra-small text-muted">{{ m.email }}</div>
                                </b-col>
                                <b-col class="px-2 grid-col-role text-left">
                                    <div class="mobile-col-caption d-lg-none">Role</div>
                                    <b-form-select v-if="isTeamOwnerOrAdmin(managingTeam) && m.role !== 0 && m.role !== 'Owner'"
                                        v-model="m.role" :options="wsRoleOptions" size="sm"
                                        data-test="select-member-role"
                                        @change="saveMemberSettingsAsync(m)"></b-form-select>
                                    <b-badge v-else :variant="m.role === 0 || m.role === 'Owner' ? 'primary' : 'secondary'">
                                        {{ wsRoleName(m.role) }}
                                    </b-badge>
                                </b-col>
                                <b-col class="px-2 grid-col-capacity text-left">
                                    <div class="mobile-col-caption d-lg-none">Capacity</div>
                                    <b-input-group v-if="isTeamOwnerOrAdmin(managingTeam)" size="sm" append="h/d">
                                        <b-form-input v-model.number="m.dailyCapacityOverride" type="number" min="1" max="16"
                                            placeholder="8" data-test="input-member-capacity"
                                            @change="saveMemberSettingsAsync(m)"></b-form-input>
                                    </b-input-group>
                                    <span v-else class="small">{{ m.dailyCapacityOverride || m.effectiveDailyCapacity || 8 }}h / day</span>
                                </b-col>
                                <b-col class="px-2 grid-col-identity text-left">
                                    <div class="mobile-col-caption d-lg-none">External Identity</div>
                                    <b-form-input v-if="isTeamOwnerOrAdmin(managingTeam)"
                                        v-model.trim="m.externalIdentity" size="sm"
                                        placeholder="Auto-detected from email"
                                        data-test="input-member-identity"
                                        @change="saveMemberSettingsAsync(m)"></b-form-input>
                                    <span v-else class="small font-monospace text-muted">{{ m.externalIdentity || m.email }}</span>
                                </b-col>
                                <b-col class="px-2 grid-col-actions text-right">
                                    <b-button v-if="isTeamOwnerOrAdmin(managingTeam) && m.role !== 0 && m.role !== 'Owner'"
                                        variant="outline-danger" size="sm"
                                        data-test="btn-remove-member"
                                        title="Remove member from squad"
                                        @click="wsRemoveMemberAsync(m)">
                                        <i class="bi bi-person-x"></i> Remove
                                    </b-button>
                                </b-col>
                            </b-row>
                        </b-card>
                    </grid-shell>
                </div>

                <!-- Outgoing Pending Invitations -->
                <div v-if="managingTeamInvitations.length" data-test="list-pending-invites">
                    <h6 class="font-weight-bold text-dark small text-uppercase mb-2">
                        <i class="bi bi-hourglass-split text-warning mr-1"></i>Pending Outgoing Invitations ({{ managingTeamInvitations.length }})
                    </h6>
                    <div class="mb-3">
                        <grid-shell :columns="pendingInviteGridColumns" :empty="!managingTeamInvitations.length" :can-reset="false" empty-title="No Pending Invitations">
                            <b-card v-for="inv in managingTeamInvitations" :key="inv.invitationId" class="my-1 py-1 shadow-none border">
                                <b-row class="my-1 grid-row align-items-center" data-test="row-pending-invite">
                                    <b-col class="px-2 grid-col-invitee text-left">
                                        <strong class="small text-dark">{{ inv.inviteeEmail }}</strong>
                                        <b-badge variant="light" class="border ml-2">{{ wsRoleName(inv.role) }}</b-badge>
                                    </b-col>
                                    <b-col class="px-2 grid-col-sent text-left extra-small text-muted">
                                        <div class="mobile-col-caption d-lg-none">Sent Date</div>
                                        Sent {{ formatDate(inv.createdAt) }}
                                    </b-col>
                                    <b-col class="px-2 grid-col-actions text-right">
                                        <b-button variant="outline-primary" size="sm" class="mr-2"
                                            data-test="btn-copy-single-invite"
                                            @click="copyShareableInviteLink(inv.token)">
                                            <i class="bi bi-clipboard mr-1"></i>Copy Invite Link
                                        </b-button>
                                        <b-button variant="outline-danger" size="sm"
                                            data-test="btn-revoke-invite"
                                            @click="revokeInvitationAsync(inv)">
                                            <i class="bi bi-x-circle mr-1"></i>Revoke
                                        </b-button>
                                    </b-col>
                                </b-row>
                            </b-card>
                        </grid-shell>
                    </div>
                </div>

                <div class="d-flex justify-content-end pt-2 border-top">
                    <b-button variant="secondary" @click="showManageTeamModal = false" data-test="btn-close-manage-team">Done</b-button>
                </div>
            </div>
        </b-modal>

        <!-- ==================== 4-STEP ADD / EDIT PROJECT WIZARD MODAL ==================== -->
        <b-modal v-model="showWizardModal" :title="isEditingProject ? 'Edit Project Space Settings' : 'Add Project Space'" centered
            hide-footer size="lg" no-close-on-backdrop @hidden="resetWizard" data-test="modal-project-wizard" :data-step="wizardStep">
            <!-- Stepper Indicator -->
            <div class="wizard-stepper mb-4 d-flex justify-content-between align-items-center">
                <div class="wizard-step" :class="{ 'active': wizardStep === 1, 'completed': wizardStep > 1 }">
                    <div class="step-icon">
                        <i v-if="wizardStep > 1" class="bi bi-check-lg"></i>
                        <span v-else>1</span>
                    </div>
                    <div class="step-label">Backlog</div>
                </div>
                <div class="wizard-step-line" :class="{ 'completed': wizardStep > 1 }"></div>
                <div class="wizard-step" :class="{ 'active': wizardStep === 2, 'completed': wizardStep > 2 }">
                    <div class="step-icon">
                        <i v-if="wizardStep > 2" class="bi bi-check-lg"></i>
                        <span v-else>2</span>
                    </div>
                    <div class="step-label">Repositories</div>
                </div>
                <div class="wizard-step-line" :class="{ 'completed': wizardStep > 2 }"></div>
                <div class="wizard-step" :class="{ 'active': wizardStep === 3, 'completed': wizardStep > 3 }">
                    <div class="step-icon">
                        <i v-if="wizardStep > 3" class="bi bi-check-lg"></i>
                        <span v-else>3</span>
                    </div>
                    <div class="step-label">Details</div>
                </div>
                <div class="wizard-step-line" :class="{ 'completed': wizardStep > 3 }"></div>
                <div class="wizard-step" :class="{ 'active': wizardStep === 4 }">
                    <div class="step-icon">
                        <span>4</span>
                    </div>
                    <div class="step-label">Confirm</div>
                </div>
            </div>

            <!-- STEP 1: Backlog Source (Optional) -->
            <div v-if="wizardStep === 1">
                <div class="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h6 class="font-weight-bold text-dark mb-0">Backlog & Task Tracking</h6>
                        <small class="text-muted">Link a cloud backlog to track work items, tasks, and sprints.</small>
                    </div>
                    <b-form-checkbox v-model="wizardForm.enableBacklog" switch class="mb-0" @change="onWizardEnableBacklogChange" data-test="switch-wizard-backlog">
                        <span class="small font-weight-bold">{{ wizardForm.enableBacklog ? 'Enabled' : 'Skip' }}</span>
                    </b-form-checkbox>
                </div>

                <div v-if="wizardForm.enableBacklog">
                    <!-- Contextual Explanation Card -->
                    <div class="alert alert-light border small py-2 px-3 mb-3 d-flex align-items-start">
                        <i class="bi bi-kanban text-primary mr-2 mt-1 fa-lg"></i>
                        <div>
                            <strong class="text-dark">Why connect your backlog?</strong>
                            <div class="text-muted mt-1" style="line-height: 1.45;">
                                Linking a cloud backlog synchronizes active sprints, user stories, child tasks, and estimates into <strong>Timelog</strong> and <strong>Backlog</strong> so you can log hours and update task progress from one centralized cockpit.
                            </div>
                        </div>
                    </div>

                    <!-- Platform Link Selector -->
                    <b-form-group label="Backlog Platform Connection" label-for="w-backlog-link">
                        <b-input-group>
                            <b-form-select id="w-backlog-link" v-model="wizardForm.backlogLinkId"
                                data-test="select-wizard-backlog-link"
                                @change="onWizardBacklogLinkChange">
                                <b-form-select-option :value="null" disabled>-- Choose a platform link
                                    --</b-form-select-option>
                                <b-form-select-option v-for="l in backlogEligibleLinks" :key="l.id" :value="l.id">
                                    {{ l.name }} ({{ platformTypeName(l.platformType) }} - {{ l.tenantSlug }})
                                </b-form-select-option>
                            </b-form-select>
                            <b-input-group-append>
                                <b-button variant="outline-primary" @click="openAddPlatformLinkModal"
                                    title="Add a new platform link" aria-label="Add a new platform link" data-test="btn-wizard-add-link">
                                    <i class="bi bi-plus-lg"></i>
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                        <div v-if="!backlogEligibleLinks.length" class="small text-warning mt-1">
                            <i class="bi bi-info-circle mr-1"></i>No backlog-capable platform links found. Click "+" to
                            connect one.
                        </div>
                        <div v-else-if="wizardSelectedBacklogLink" class="alert alert-light border small mt-2 mb-0 py-2 px-3 d-flex align-items-start">
                            <i class="bi bi-shield-lock-fill text-info mr-2 mt-1"></i>
                            <div>
                                <strong>Required {{ platformTypeName(wizardSelectedBacklogLink.platformType) }} Backlog Permissions:</strong>
                                <div class="mt-1" v-html="wizardBacklogPermissionsHtml"></div>
                            </div>
                        </div>
                    </b-form-group>

                    <!-- Backlog Platform Project Picker -->
                    <b-form-group v-if="wizardForm.backlogLinkId" label="Backlog Platform Project" label-for="w-backlog-proj">
                        <b-overlay :show="wizardLoadingProjects">
                            <b-form-select id="w-backlog-proj" v-model="wizardForm.selectedBacklogProject"
                                data-test="select-wizard-backlog-project"
                                :data-state="wizardLoadingProjects ? 'loading' : 'idle'"
                                @change="onWizardBacklogProjectChange">
                                <b-form-select-option :value="null" disabled>-- Select platform project --</b-form-select-option>
                                <b-form-select-option v-for="p in wizardAvailableProjects" :key="p.id" :value="p">
                                    {{ p.name }}
                                </b-form-select-option>
                            </b-form-select>
                            <template #overlay>
                                <load-spinner size="sm" inline text="Loading platform projects..." />
                            </template>
                        </b-overlay>
                        <div v-if="!wizardLoadingProjects && !wizardAvailableProjects.length" class="small text-muted mt-1">
                            <i class="bi bi-info-circle mr-1"></i>No platform projects loaded for this platform connection.
                            <b-button variant="link" size="sm" class="p-0 ml-1 text-decoration-none" @click="loadWizardBacklogProjects(wizardForm.backlogLinkId)">
                                Click here to load platform projects
                            </b-button>
                        </div>
                    </b-form-group>

                    <!-- Searchable Team Picker -->
                    <b-form-group v-if="wizardForm.selectedBacklogProject" label="Team (Required)"
                        label-for="w-team-select">
                        <b-overlay :show="wizardLoadingTeams">
                            <vue-multiselect id="w-team-select" v-model="wizardForm.selectedTeam"
                                :options="wizardAvailableTeams" track-by="name" label="name" :multiple="!isEditingProject"
                                :searchable="true" :close-on-select="isEditingProject"
                                data-test="select-wizard-team"
                                :data-state="wizardLoadingTeams ? 'loading' : 'idle'"
                                placeholder="Search teams in this platform project..."></vue-multiselect>
                            <template #overlay>
                                <load-spinner size="sm" inline text="Loading teams..." />
                            </template>
                        </b-overlay>
                        <small class="text-muted">All teams from the selected platform project are listed with your teams at
                            top.</small>
                    </b-form-group>
                </div>
                <div v-else class="alert alert-secondary border small my-4">
                    <i class="bi bi-info-circle mr-1"></i>Backlog tracking skipped. You can configure a backlog source later.
                </div>

                <div class="d-flex justify-content-between pt-3 border-top">
                    <b-button variant="secondary" @click="showWizardModal = false" data-test="btn-wizard-cancel">Cancel</b-button>
                    <b-button variant="primary"
                        :disabled="wizardForm.enableBacklog && (!wizardForm.backlogLinkId || !wizardForm.selectedBacklogProject || (isEditingProject ? !wizardForm.selectedTeam : (!wizardForm.selectedTeam || wizardForm.selectedTeam.length === 0)))"
                        data-test="btn-wizard-next"
                        @click="wizardStep = 2">
                        Next: Repositories <i class="bi bi-arrow-right ml-1"></i>
                    </b-button>
                </div>
            </div>

            <!-- STEP 2: Repository Source (Optional) -->
            <div v-if="wizardStep === 2">
                <div class="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h6 class="font-weight-bold text-dark mb-0">Repository & PR Source</h6>
                        <small class="text-muted">Link code repositories for pull request tracking and activity
                            metrics.</small>
                    </div>
                    <b-form-checkbox v-model="wizardForm.enableRepos" switch class="mb-0" @change="onWizardEnableReposChange" data-test="switch-wizard-repos">
                        <span class="small font-weight-bold">{{ wizardForm.enableRepos ? 'Enabled' : 'Skip' }}</span>
                    </b-form-checkbox>
                </div>

                <div v-if="wizardForm.enableRepos">
                    <!-- Contextual Explanation Card -->
                    <div class="alert alert-light border small py-2 px-3 mb-3 d-flex align-items-start">
                        <i class="bi bi-git text-danger mr-2 mt-1 fa-lg"></i>
                        <div>
                            <strong class="text-dark">Why connect your repository?</strong>
                            <div class="text-muted mt-1" style="line-height: 1.45;">
                                Linking code repositories enables <strong>Pull Requests</strong> and <strong>Branch Hygiene</strong> to track reviewer approval votes, quality gate blockers, merge readiness, and cleanup stale branches linked to your active sprint tasks.
                            </div>
                        </div>
                    </div>

                    <!-- Same as backlog checkbox (if backlog is configured & supports repos) -->
                    <b-form-checkbox v-if="canMirrorBacklogToRepo" v-model="wizardForm.sameAsBacklog" class="mb-3" data-test="chk-wizard-same-as-backlog">
                        <span class="font-weight-500 text-dark small">
                            Use same repository source as backlog ({{ wizardForm.selectedBacklogProject ?
                                wizardForm.selectedBacklogProject.name : '' }})
                        </span>
                    </b-form-checkbox>

                    <div>
                        <!-- Platform Link Selector -->
                        <b-form-group label="Repository Platform Connection" label-for="w-repo-link">
                            <b-input-group>
                                <b-form-select id="w-repo-link" v-model="wizardEffectiveRepoLinkId"
                                    data-test="select-wizard-repo-link"
                                    :disabled="wizardForm.sameAsBacklog && canMirrorBacklogToRepo" @change="onWizardRepoLinkChange">
                                    <b-form-select-option :value="null" disabled>-- Choose a repository connection
                                        --</b-form-select-option>
                                    <b-form-select-option v-for="l in repoEligibleLinks" :key="l.id" :value="l.id">
                                        {{ l.name }} ({{ platformTypeName(l.platformType) }} - {{ l.tenantSlug }})
                                    </b-form-select-option>
                                </b-form-select>
                                <b-input-group-append>
                                    <b-button variant="outline-primary" :disabled="wizardForm.sameAsBacklog && canMirrorBacklogToRepo"
                                        title="Add a new platform link" aria-label="Add a new platform link" data-test="btn-wizard-add-repo-link"
                                        @click="openAddPlatformLinkModal">
                                        <i class="bi bi-plus-lg"></i>
                                    </b-button>
                                </b-input-group-append>
                            </b-input-group>
                            <div v-if="wizardSelectedRepoLink" class="alert alert-light border small mt-2 mb-0 py-2 px-3 d-flex align-items-start">
                                <i class="bi bi-shield-lock-fill text-info mr-2 mt-1"></i>
                                <div>
                                    <strong>Required {{ platformTypeName(wizardSelectedRepoLink.platformType) }} Repository Permissions:</strong>
                                    <div class="mt-1" v-html="wizardRepoPermissionsHtml"></div>
                                </div>
                            </div>
                        </b-form-group>

                        <!-- Repo Project Picker -->
                        <b-form-group label="Repository Platform Project / Org" label-for="w-repo-proj">
                            <b-overlay :show="wizardLoadingRepoProjects">
                                <b-form-select id="w-repo-proj" v-model="wizardEffectiveRepoProject"
                                    data-test="select-wizard-repo-project"
                                    :data-state="wizardLoadingRepoProjects ? 'loading' : 'idle'"
                                    :disabled="wizardForm.sameAsBacklog && canMirrorBacklogToRepo"
                                    @change="onWizardRepoProjectChange">
                                    <b-form-select-option :value="null" disabled>-- Select repository project
                                        --</b-form-select-option>
                                    <b-form-select-option v-for="p in wizardDisplayRepoProjects" :key="p.id" :value="p">
                                        {{ p.name }}
                                    </b-form-select-option>
                                </b-form-select>
                                <template #overlay>
                                    <load-spinner size="sm" inline text="Loading repositories..." />
                                </template>
                            </b-overlay>
                            <div v-if="!wizardLoadingRepoProjects && !wizardDisplayRepoProjects.length" class="small text-muted mt-1">
                                <i class="bi bi-info-circle mr-1"></i>No repositories loaded for this connection.
                                <b-button variant="link" size="sm" class="p-0 ml-1 text-decoration-none" @click="onWizardRepoLinkChange(wizardEffectiveRepoLinkId)">
                                    Click here to load repositories
                                </b-button>
                            </div>
                        </b-form-group>
                    </div>
                </div>
                <div v-else class="alert alert-secondary border small my-4">
                    <i class="bi bi-info-circle mr-1"></i>Repository tracking skipped.
                </div>

                <div v-if="!wizardForm.enableBacklog && !wizardForm.enableRepos" class="alert alert-warning border small my-3">
                    <i class="bi bi-exclamation-triangle-fill mr-1"></i>A project space must have at least one source (Backlog or Repository) enabled.
                </div>

                <div class="d-flex justify-content-between pt-3 border-top">
                    <div>
                        <b-button variant="secondary" class="mr-2" @click="wizardStep = 1" data-test="btn-wizard-back">
                            <i class="bi bi-arrow-left mr-1"></i> Back
                        </b-button>
                        <b-button variant="outline-secondary" @click="showWizardModal = false" data-test="btn-wizard-cancel">Cancel</b-button>
                    </div>
                    <b-button variant="primary"
                        :disabled="(!wizardForm.enableBacklog && !wizardForm.enableRepos) || (wizardForm.enableRepos && (!wizardForm.sameAsBacklog || !canMirrorBacklogToRepo) && (!wizardEffectiveRepoLinkId || !wizardEffectiveRepoProject))"
                        data-test="btn-wizard-next"
                        @click="goToWizardStep3">
                        Next: Details <i class="bi bi-arrow-right ml-1"></i>
                    </b-button>
                </div>
            </div>

            <!-- STEP 3: Details -->
            <div v-if="wizardStep === 3">
                <h6 class="font-weight-bold text-dark mb-1">Project Space Details & Capacity</h6>
                <p class="text-muted small mb-3">Configure working capacity and roles for this project space.</p>

                <b-form-group label="Project Space Name" label-for="w-proj-name"
                    description="Name displayed inside VelocyTrack.">
                    <b-form-input id="w-proj-name" v-model.trim="wizardForm.projectName"
                        :disabled="isEditingMemberPreferences || (!useCustomProjectName && !!defaultProjectNameSuggestion)"
                        :placeholder="defaultProjectNameSuggestion || 'E.g. Payment Gateway or Core API'"
                        data-test="input-wizard-name"
                        required></b-form-input>
                    <b-form-checkbox v-if="defaultProjectNameSuggestion && !isEditingMemberPreferences" v-model="useCustomProjectName" class="mt-2 text-muted small" @change="onCustomProjectNameToggle" data-test="chk-wizard-custom-name">
                        Use different display name
                    </b-form-checkbox>
                </b-form-group>

                <b-row>
                    <b-col md="6">
                        <b-form-group label="Daily Capacity (Hours)" label-for="w-proj-cap"
                            description="Expected working hours per day for capacity.">
                            <b-form-input id="w-proj-cap" v-model.number="wizardForm.dailyCapacity" type="number"
                                min="1" max="16" required data-test="input-wizard-capacity"></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col md="6">
                        <b-form-group label="Functional Roles" label-for="w-proj-roles"
                            description="Functional roles active on this project space.">
                            <div class="role-multiselect-box p-2 rounded border bg-light" data-test="wizard-role-group">
                                <b-form-radio v-model.number="wizardForm.employeeRoleOverride" :value="0" name="w-proj-role" class="mb-2" data-test="radio-wizard-role-dev">
                                    <span class="font-weight-500 text-dark">Developer</span>
                                    <span v-if="Number(wizardForm.employeeRoleOverride) === 0" class="badge badge-success ml-2 extra-small">Active</span>
                                </b-form-radio>
                                <b-form-radio v-model.number="wizardForm.employeeRoleOverride" :value="1" name="w-proj-role" class="mb-2" data-test="radio-wizard-role-qa">
                                    <span class="font-weight-500 text-dark">Quality Assurance (QA)</span>
                                    <span v-if="Number(wizardForm.employeeRoleOverride) === 1" class="badge badge-success ml-2 extra-small">Active</span>
                                </b-form-radio>
                                <b-form-radio v-model.number="wizardForm.employeeRoleOverride" :value="2" name="w-proj-role" data-test="radio-wizard-role-sa">
                                    <span class="font-weight-500 text-dark">System Analyst</span>
                                    <span v-if="Number(wizardForm.employeeRoleOverride) === 2" class="badge badge-success ml-2 extra-small">Active</span>
                                </b-form-radio>
                            </div>
                        </b-form-group>
                    </b-col>
                </b-row>

                <div v-if="isEditingProject && !isEditingMemberPreferences && wizardForm.enableBacklog" class="mb-3" data-test="section-backlog-field-map">
                    <b-button variant="link" size="sm" class="p-0 text-decoration-none"
                        data-test="btn-toggle-field-map"
                        :aria-expanded="wizardFieldMapOpen ? 'true' : 'false'"
                        @click="toggleWizardFieldMap">
                        <i class="bi mr-1" :class="wizardFieldMapOpen ? 'bi-chevron-down' : 'bi-chevron-right'"></i>Backlog field mapping
                    </b-button>
                    <b-badge v-if="hasFieldMapOverrides" variant="info" class="ml-2 extra-small" data-test="badge-field-map-customized">Customized</b-badge>
                    <b-collapse v-model="wizardFieldMapOpen">
                        <p class="text-muted small mt-2 mb-2">
                            Role and custom fields are detected automatically from this project. Override a field only when the detection picks the wrong one.
                        </p>
                        <div v-if="wizardFieldMapLoading" class="text-muted small py-2" data-state="loading">Loading project fields...</div>
                        <b-alert :show="!!wizardFieldMapError" variant="warning" class="small" data-test="alert-field-map-error">{{ wizardFieldMapError }}</b-alert>
                        <b-row v-if="!wizardFieldMapLoading">
                            <b-col md="6" v-for="slot in wizardFieldMapSlots" :key="slot.key">
                                <b-form-group :label="slot.label" :label-for="'w-fm-' + slot.key" :description="fieldMapHint(slot)" class="mb-2">
                                    <b-form-select :id="'w-fm-' + slot.key" size="sm"
                                        v-model="wizardForm.backlogFieldMap[slot.key]"
                                        :options="fieldMapOptions(slot)"
                                        :data-test="'select-field-map-' + slot.key"></b-form-select>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-collapse>
                </div>

                <b-form-checkbox v-model="wizardForm.isPrimary" class="mt-2 mb-3" data-test="chk-wizard-primary">
                    <span class="small font-weight-500 text-dark">Make this the default primary project space</span>
                </b-form-checkbox>

                <div class="d-flex justify-content-between pt-3 border-top">
                    <div>
                        <b-button v-if="!isEditingMemberPreferences" variant="secondary" class="mr-2" @click="wizardStep = 2" data-test="btn-wizard-back">
                            <i class="bi bi-arrow-left mr-1"></i> Back
                        </b-button>
                        <b-button variant="outline-secondary" @click="showWizardModal = false" data-test="btn-wizard-cancel">Cancel</b-button>
                    </div>
                    <b-button variant="primary" :disabled="!wizardForm.projectName" @click="wizardStep = 4" data-test="btn-wizard-next">
                        Next: Review <i class="bi bi-arrow-right ml-1"></i>
                    </b-button>
                </div>
            </div>

            <!-- STEP 4: Review & Confirm -->
            <div v-if="wizardStep === 4">
                <h6 class="font-weight-bold text-dark mb-1">Confirm Project Space Setup</h6>
                <p class="text-muted small mb-3">Review your project space configuration before {{ isEditingProject ?
                    'updating it' :
                    'creating it' }}.</p>

                <div class="review-card p-3 rounded bg-light border mb-4">
                    <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <span class="text-muted small">Project Space Name</span>
                        <strong class="text-dark">{{ wizardForm.projectName }}</strong>
                    </div>

                    <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <span class="text-muted small">Backlog Source</span>
                        <div class="text-right">
                            <span v-if="wizardForm.enableBacklog && wizardForm.selectedBacklogProject">
                                <b-badge v-if="wizardSelectedBacklogLink"
                                    :variant="platformBadgeVariant(wizardSelectedBacklogLink.platformType)"
                                    class="px-2 py-1 mr-2">
                                    <i :class="platformIconOnly(wizardSelectedBacklogLink.platformType)"
                                        class="mr-1"></i>{{
                                            wizardSelectedBacklogLink.name }}
                                </b-badge>
                                <strong>{{ wizardForm.selectedBacklogProject.name }}</strong>
                                <span v-if="wizardForm.selectedTeam && isEditingProject" class="text-muted small"> (Team: {{
                                    wizardForm.selectedTeam.name }})</span>
                                <span v-else-if="wizardForm.selectedTeam && wizardForm.selectedTeam.length > 0" class="text-muted small"> (Teams: {{
                                    wizardForm.selectedTeam.map(t => t.name).join(', ') }})</span>
                            </span>
                            <span v-else class="text-muted small">None (Skipped)</span>
                        </div>
                    </div>

                    <div class="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                        <span class="text-muted small">Repository Source</span>
                        <div class="text-right">
                            <span v-if="wizardForm.enableRepos && wizardEffectiveRepoProject">
                                <b-badge v-if="wizardSelectedRepoLink"
                                    :variant="platformBadgeVariant(wizardSelectedRepoLink.platformType)"
                                    class="px-2 py-1 mr-2">
                                    <i :class="platformIconOnly(wizardSelectedRepoLink.platformType)"
                                        class="mr-1"></i>{{
                                            wizardSelectedRepoLink.name }}
                                </b-badge>
                                <strong>{{ wizardEffectiveRepoProject.name }}</strong>
                            </span>
                            <span v-else class="text-muted small">None (Skipped)</span>
                        </div>
                    </div>

                    <div class="d-flex justify-content-between align-items-center">
                        <span class="text-muted small">Daily Capacity & Role</span>
                        <span>
                            <strong>{{ wizardForm.dailyCapacity }}h / day</strong> &bull;
                            {{ roleName(wizardForm.employeeRoleOverride ?? currentUserDefaultRole ?? 0) }}
                        </span>
                    </div>
                </div>

                <div class="d-flex justify-content-between pt-3 border-top">
                    <div>
                        <b-button variant="secondary" class="mr-2" @click="wizardStep = 3" data-test="btn-wizard-back">
                            <i class="bi bi-arrow-left mr-1"></i> Back
                        </b-button>
                        <b-button variant="outline-secondary" @click="showWizardModal = false" data-test="btn-wizard-cancel">Cancel</b-button>
                    </div>
                    <b-button variant="primary" class="px-4 shadow-sm" :disabled="wizardSavingProject"
                        data-test="btn-wizard-save"
                        :data-state="wizardSavingProject ? 'loading' : 'idle'"
                        @click="saveWizardProjectAsync">
                        <b-spinner v-if="wizardSavingProject" small class="mr-1"></b-spinner>
                        <i v-else class="bi bi-check2-circle mr-1"></i> {{ isEditingProject ? 'Save Changes' : 'Create Project Space' }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- MODAL: ADD / EDIT PLATFORM LINK -->
        <modal-platform-link ref="platformLinkModal" @saved="onPlatformLinkSaved"></modal-platform-link>
    </div>
</template>

<script>
import eventBus from '../event-bus';
import ValidationMessages from './validation-messages.vue';
import sharedMixin from '../mixinAccount';
import toastMixin from '../mixinToast';
import ModalPlatformLink from './modal-platform-link.vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import GridShell from './grid-shell.vue';

const FIELD_MAP_SLOTS = [
    { key: 'devAssignedTo', label: 'Developer assigned to', kind: 'identity' },
    { key: 'qaAssignedTo', label: 'QA assigned to', kind: 'identity' },
    { key: 'saAssignedTo', label: 'System analyst assigned to', kind: 'identity' },
    { key: 'devEffort', label: 'Developer effort', kind: 'number' },
    { key: 'qaEffort', label: 'QA effort', kind: 'number' },
    { key: 'saEffort', label: 'System analyst effort', kind: 'number' }
];
const emptyFieldMap = () => FIELD_MAP_SLOTS.reduce((acc, s) => ({ ...acc, [s.key]: null }), {});

export default {
    name: 'PageConfig',
    mixins: [sharedMixin, toastMixin],
    components: {
        'validation-messages': ValidationMessages,
        'modal-platform-link': ModalPlatformLink,
        'vue-multiselect': Multiselect,
        'grid-shell': GridShell
    },
    data() {
        return {
            rosterGridColumns: [
                { key: 'member', label: 'Member', width: 'minmax(200px, 2.2fr)', headerClass: 'text-left' },
                { key: 'role', label: 'Role', width: '150px', headerClass: 'text-left' },
                { key: 'capacity', label: 'Daily Capacity', width: '140px', headerClass: 'text-left' },
                { key: 'identity', label: 'Cloud Identity (Task/PR Match)', width: 'minmax(180px, 1.8fr)', headerClass: 'text-left' },
                { key: 'actions', label: 'Actions', width: '120px', headerClass: 'text-right' }
            ],
            pendingInviteGridColumns: [
                { key: 'invitee', label: 'Invited Teammate', width: 'minmax(200px, 2.5fr)', headerClass: 'text-left' },
                { key: 'sent', label: 'Sent', width: '140px', headerClass: 'text-left' },
                { key: 'actions', label: 'Actions', width: '120px', headerClass: 'text-right' }
            ],
            activeConfigTab: 0,
            projectsList: [],
            platformLinks: [],
            loadingProjects: true,
            loadingPlatformLinks: true,
            archivedCount: 0,
            verifyingLinkId: null,

            // User preferences
            currentUserStartLogDate: null,
            currentUserDailyCapacity: 8,
            currentUserDefaultRole: 0,
            savingSettings: false,
            settingsForm: {
                dailyCapacity: 8,
                startLogDate: null,
                employeeRole: 0
            },

            // 4-Step Project Wizard (Add / Edit)
            showWizardModal: false,
            isEditingProject: false,
            isEditingMemberPreferences: false,
            editingProjectId: null,
            wizardStep: 1,
            wizardLoadingProjects: false,
            wizardAvailableProjects: [],
            wizardLoadingTeams: false,
            wizardAvailableTeams: [],
            wizardLoadingRepoProjects: false,
            wizardAvailableRepoProjects: [],
            wizardSavingProject: false,
            wizardFieldMapSlots: FIELD_MAP_SLOTS,
            wizardFieldMapOpen: false,
            wizardFieldMapLoaded: false,
            wizardFieldMapLoading: false,
            wizardFieldMapFields: [],
            wizardFieldMapResolved: null,
            wizardFieldMapError: '',
            useCustomProjectName: false,
            wizardForm: {
                projectName: '',
                dailyCapacity: 8,
                employeeRoleOverride: 0,
                selectedRoles: [0],
                isPrimary: false,
                backlogFieldMap: emptyFieldMap(),
                enableBacklog: true,
                backlogLinkId: null,
                selectedBacklogProject: null,
                selectedTeam: null,
                enableRepos: true,
                sameAsBacklog: true,
                repoLinkId: null,
                selectedRepoProject: null
            },

            // Workspace & Teams
            wsLoading: false,
            wsWorkspace: null,
            teamsList: [],
            incomingInvitations: [],
            respondingInviteToken: null,
            showCreateTeamModal: false,
            isEditingTeam: false,
            editingTeamId: null,
            savingTeam: false,
            teamForm: {
                name: '',
                description: '',
                seatSlots: 1,
                projectSpaceIds: []
            },
            teamSlotSelectOptions: [
                { value: 1, text: '1 Slot — 5 Seats (Base)' },
                { value: 2, text: '2 Slots — 10 Seats (+1 slot)' },
                { value: 3, text: '3 Slots — 15 Seats (+2 slots)' },
                { value: 4, text: '4 Slots — 20 Seats (+3 slots)' },
                { value: 5, text: '5 Slots — 25 Seats (+4 slots)' },
                { value: 6, text: '6 Slots — 30 Seats (+5 slots)' }
            ],
            showManageTeamModal: false,
            managingTeam: null,
            managingTeamInvitations: [],
            addingSeatSlot: false,
            wsMembers: [],
            wsInviting: false,
            wsInviteEmail: '',
            wsInviteRole: 2,
            wsInviteCapacity: 8,
            wsMemberFields: [
                { key: 'user', label: 'User' },
                { key: 'role', label: 'Workspace Role' },
                { key: 'actions', label: '', class: 'text-right' }
            ],
            wsRoleOptions: [
                { value: 1, text: 'Admin' },
                { value: 2, text: 'Member' },
                { value: 3, text: 'Read-Only' }
            ]
        };
    },
    computed: {
        isSelectedTeamQuotaFull() {
            if (!this.managingTeam) return false;
            if (this.managingTeam.isSeatQuotaFull) return true;
            const limit = this.managingTeam.seatLimit || 5;
            const activeInvited = (this.wsMembers && this.wsMembers.filter(m => m.isActive !== false && m.role !== 0 && m.role !== 'Owner').length) || (this.managingTeam.activeMembersCount ?? 0);
            const pending = (this.managingTeamInvitations && this.managingTeamInvitations.length) || (this.managingTeam.pendingInvitationsCount ?? 0);
            return (activeInvited + pending) >= limit;
        },
        canCreateNewTeamSpace() {
            return this.availableSeatSlots >= 1;
        },
        availableTeamSlotOptions() {
            if (this.isBetaUser) {
                return [{ value: 1, text: '1 Batch Slot — 5 Seats (Included in Beta Plan)' }];
            }
            const maxSlots = this.isEditingTeam
                ? Math.max(1, (this.teamForm.seatSlots || 1) + this.availableSeatSlots)
                : Math.max(1, this.availableSeatSlots);
            const opts = [];
            for (let k = 1; k <= maxSlots; k++) {
                opts.push({
                    value: k,
                    text: `${k} ${k === 1 ? 'Batch Slot' : 'Batch Slots'} — ${k * 5} Seats (Allocates ${k} of ${maxSlots} available)`
                });
            }
            return opts;
        },
        personalOrAllProjectsForSharing() {
            return (this.projectsList || []).filter(p => !p.isArchived);
        },
        hasFieldMapOverrides() {
            return Object.values((this.wizardForm && this.wizardForm.backlogFieldMap) || {}).some(v => !!v);
        },
        backlogEligibleLinks() {
            return this.platformLinks.filter(l => l.supportsBacklog);
        },
        repoEligibleLinks() {
            return this.platformLinks.filter(l => l.supportsRepos);
        },
        wizardSelectedBacklogLink() {
            if (!this.wizardForm.backlogLinkId) return null;
            return this.platformLinks.find(l => l.id === this.wizardForm.backlogLinkId) || null;
        },
        wizardSelectedRepoLink() {
            const id = this.wizardEffectiveRepoLinkId;
            if (!id) return null;
            return this.platformLinks.find(l => l.id === id) || null;
        },
        wizardBacklogPermissionsHtml() {
            if (!this.wizardSelectedBacklogLink) return '';
            switch (this.wizardSelectedBacklogLink.platformType) {
                case 0:
                    return '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>Work Items: Read &amp; Write</span> ' +
                           '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>Project &amp; Team: Read</span>';
                case 1:
                    return '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>read:jira-work</span> ' +
                           '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>write:jira-work</span>';
                case 2:
                    return '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>Issues: Read &amp; Write</span> ' +
                           '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>Projects: Read &amp; Write (Optional)</span>';
                case 3:
                    return '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>api</span> ' +
                           '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>read_repository</span> ' +
                           '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>write_repository</span>';
                default:
                    return '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>Read &amp; Write Access</span>';
            }
        },
        wizardRepoPermissionsHtml() {
            if (!this.wizardSelectedRepoLink) return '';
            switch (this.wizardSelectedRepoLink.platformType) {
                case 0:
                    return '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>Code: Read</span> ' +
                           '<span class="badge badge-warning text-dark border mr-1"><i class="bi bi-info-circle-fill mr-1"></i>Code: Read &amp; write (Optional)</span>';
                case 2:
                    return '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>Pull Requests: Read &amp; Write</span> ' +
                           '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>Contents: Read</span> ' +
                           '<span class="badge badge-warning text-dark border mr-1" data-test="badge-optional-repo-write"><i class="bi bi-info-circle-fill mr-1"></i>Contents: Read &amp; write (Optional)</span>';
                case 3:
                    return '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>api</span> ' +
                           '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>read_repository</span> ' +
                           '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>write_repository</span>';
                default:
                    return '<span class="badge badge-light border text-dark mr-1"><i class="bi bi-check2 text-success mr-1"></i>Read &amp; Write Access</span>';
            }
        },
        canMirrorBacklogToRepo() {
            if (!this.wizardForm.enableBacklog || !this.wizardForm.backlogLinkId) return false;
            const link = this.platformLinks.find(l => l.id === this.wizardForm.backlogLinkId);
            return !!(link && link.supportsRepos);
        },
        wizardEffectiveRepoLinkId: {
            get() {
                if (this.wizardForm.sameAsBacklog && this.canMirrorBacklogToRepo) {
                    return this.wizardForm.backlogLinkId;
                }
                return this.wizardForm.repoLinkId;
            },
            set(val) {
                this.wizardForm.repoLinkId = val;
            }
        },
        wizardEffectiveRepoProject: {
            get() {
                if (this.wizardForm.sameAsBacklog && this.canMirrorBacklogToRepo) {
                    return this.wizardForm.selectedBacklogProject;
                }
                return this.wizardForm.selectedRepoProject;
            },
            set(val) {
                this.wizardForm.selectedRepoProject = val;
            }
        },
        wizardDisplayRepoProjects() {
            const list = [...this.wizardAvailableRepoProjects];
            if (this.wizardForm.sameAsBacklog && this.wizardForm.selectedBacklogProject) {
                const bProj = this.wizardForm.selectedBacklogProject;
                if (!list.some(p => p.id === bProj.id || p.name === bProj.name)) {
                    list.unshift(bProj);
                }
            }
            return list;
        },
        roleOptions() {
            return [
                { value: 0, text: 'Developer' },
                { value: 1, text: 'QA' },
                { value: 2, text: 'System Analyst' }
            ];
        },
        defaultProjectNameSuggestion() {
            if (this.wizardForm.enableBacklog && this.wizardForm.selectedBacklogProject) {
                const bProj = this.wizardForm.selectedBacklogProject;
                const existingSameProj = this.configuredProjects.some(p =>
                    (!this.isEditingProject || p.id !== this.editingProjectId) &&
                    (p.backlogProjectId === bProj.id || p.backlogProjectId === bProj.id?.toString())
                );
                const team = !this.isEditingProject
                    ? (Array.isArray(this.wizardForm.selectedTeam) && this.wizardForm.selectedTeam.length === 1 ? this.wizardForm.selectedTeam[0] : null)
                    : this.wizardForm.selectedTeam;
                if (existingSameProj && team && team.name) {
                    return `${bProj.name} (${team.name})`;
                }
                return bProj.name;
            }
            if (this.wizardForm.enableRepos && this.wizardEffectiveRepoProject) {
                return this.wizardEffectiveRepoProject.name;
            }
            return '';
        },
        wsPlanName() {
            if (!this.wsWorkspace) return 'Free';
            const plans = { 0: 'Free', 1: 'Pro Plan', 2: 'Team Plan', Free: 'Free', Solo: 'Pro Plan', Team: 'Team Plan', SmallTeam: 'Team Plan', LargeTeam: 'Team Plan' };
            return plans[this.wsWorkspace.plan] || 'Free';
        },
        wsIsOwnerOrAdmin() {
            return this.wsWorkspace && (this.wsWorkspace.currentUserRole === 0 || this.wsWorkspace.currentUserRole === 1 || this.wsWorkspace.currentUserRole === 'Owner' || this.wsWorkspace.currentUserRole === 'Admin');
        },
        wsDaysRemainingInTrial() {
            if (!this.wsWorkspace || !this.wsWorkspace.trialEndsAt) return 0;
            const diffMs = new Date(this.wsWorkspace.trialEndsAt) - new Date();
            const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
            return Math.max(0, days);
        },
        activeProjectsList() {
            return (this.projectsList || []).filter(p => !p.isArchived);
        }
    },
    async mounted() {
        if (!this.isAuthenticated) return;

        if (this.$route.query.oauth === 'success') {
            this.toastSuccess('Connected', 'OAuth connected successfully!');
            const q = { ...this.$route.query };
            delete q.oauth;
            delete q.message;
            delete q.error;
            this.$router.replace({ query: q }).catch(() => {});
        } else if (this.$route.query.oauth === 'error') {
            this.toastError('OAuth Notice', this.$route.query.message || 'Failed to connect via OAuth.');
            const q = { ...this.$route.query };
            delete q.oauth;
            delete q.message;
            delete q.error;
            this.$router.replace({ query: q }).catch(() => {});
        }

        if (this.$route.query.teamInvite) {
            const token = this.$route.query.teamInvite;
            const q = { ...this.$route.query };
            delete q.teamInvite;
            this.$router.replace({ query: q }).catch(() => {});
            await this.acceptTeamInvitationByTokenAsync(token);
        }

        if (this.$route.query.tab === 'projects' || this.$route.query.section === 'projects') {
            this.$nextTick(() => {
                this.scrollToSection('section-projects');
            });
        } else if (this.$route.query.tab === 'teams' || this.$route.query.section === 'teams') {
            this.$nextTick(() => {
                this.scrollToSection('section-teams');
            });
        }

        await Promise.all([
            this.loadProjectsAsync(),
            this.loadPlatformLinksAsync(),
            this.loadUserSettingsAsync(),
            this.loadArchivedCountAsync(),
            this.loadWorkspaceAsync()
        ]);

        const querySec = this.$route.query.section || this.$route.query.tab;
        if (querySec === 'teams') {
            this.$nextTick(() => {
                setTimeout(() => {
                    this.scrollToSection('section-teams');
                }, 150);
            });
        } else if (querySec === 'projects') {
            this.$nextTick(() => {
                setTimeout(() => {
                    this.scrollToSection('section-projects');
                }, 150);
            });
        } else if (querySec === 'connections') {
            this.$nextTick(() => {
                setTimeout(() => {
                    this.scrollToSection('section-connections');
                }, 150);
            });
        }

        if (this.$route.query.editProject && this.projectsList.length) {
            const target = this.projectsList.find(p => p.id === this.$route.query.editProject || p.projectId === this.$route.query.editProject);
            if (target) {
                this.openEditProjectModal(target, { enableBacklogIfMissing: true });
                this.$nextTick(() => {
                    this.scrollToSection('section-projects');
                });
            }
        }
    },
    watch: {
        isDevEnv(newVal) {
            if (newVal) {
                this.loadWorkspaceAsync();
            }
        },
        useCustomProjectName(val) {
            if (!val && this.wizardForm.selectedBacklogProject && !this.isEditingProject) {
                this.wizardForm.projectName = this.wizardForm.selectedBacklogProject.name;
            }
        },
        'wizardForm.enableBacklog'(val) {
            if (val) {
                if (!this.wizardForm.backlogLinkId && this.backlogEligibleLinks.length) {
                    this.wizardForm.backlogLinkId = this.backlogEligibleLinks[0].id;
                }
                if (this.wizardForm.backlogLinkId && (!this.wizardAvailableProjects || !this.wizardAvailableProjects.length)) {
                    this.loadWizardBacklogProjects(this.wizardForm.backlogLinkId);
                }
            }
        },
        'wizardForm.enableRepos'(val) {
            if (val) {
                if (!this.wizardForm.repoLinkId && this.repoEligibleLinks.length) {
                    this.wizardForm.repoLinkId = this.repoEligibleLinks[0].id;
                }
                const effectiveLinkId = this.wizardEffectiveRepoLinkId;
                if (effectiveLinkId && !this.wizardForm.sameAsBacklog && (!this.wizardAvailableRepoProjects || !this.wizardAvailableRepoProjects.length)) {
                    this.onWizardRepoLinkChange(effectiveLinkId);
                }
            }
        },
        'backlogEligibleLinks'(newLinks) {
            if (newLinks && newLinks.length && !this.wizardForm.backlogLinkId) {
                this.wizardForm.backlogLinkId = newLinks[0].id;
                if (this.showWizardModal && this.wizardForm.enableBacklog && (!this.wizardAvailableProjects || !this.wizardAvailableProjects.length)) {
                    this.loadWizardBacklogProjects(newLinks[0].id);
                }
            }
        },
        '$route.query.editProject'(newVal) {
            if (newVal && this.projectsList.length) {
                const target = this.projectsList.find(p => p.id === newVal || p.projectId === newVal);
                if (target) {
                    this.openEditProjectModal(target, { enableBacklogIfMissing: true });
                    this.$nextTick(() => {
                        this.scrollToSection('section-projects');
                    });
                }
            }
        },
        '$route.query.section'(newVal) {
            if (newVal === 'teams') {
                this.$nextTick(() => this.scrollToSection('section-teams'));
            } else if (newVal === 'projects') {
                this.$nextTick(() => this.scrollToSection('section-projects'));
            } else if (newVal === 'connections') {
                this.$nextTick(() => this.scrollToSection('section-connections'));
            }
        },
        '$route.query.tab'(newVal) {
            if (newVal === 'teams') {
                this.$nextTick(() => this.scrollToSection('section-teams'));
            } else if (newVal === 'projects') {
                this.$nextTick(() => this.scrollToSection('section-projects'));
            }
        }
    },
    methods: {
        scrollToSection(sectionId) {
            const el = document.getElementById(sectionId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
        async loadProjectsAsync() {
            this.loadingProjects = true;
            try {
                const resp = await this.$http.get('/Config/ProjectSpaces');
                this.projectsList = resp.data || [];
            } catch (err) {
                console.error('Failed to load project spaces:', err);
            } finally {
                this.loadingProjects = false;
            }
        },
        async loadPlatformLinksAsync() {
            this.loadingPlatformLinks = true;
            try {
                const resp = await this.$http.get('/Config/PlatformLinks');
                this.platformLinks = resp.data || [];
            } catch (err) {
                console.error('Failed to load platform links:', err);
                this.platformLinks = [];
            } finally {
                this.loadingPlatformLinks = false;
            }
        },
        getLinkProjectCount(linkId) {
            if (!linkId || !this.projectsList) return 0;
            return this.projectsList.filter(p => !p.isArchived && (p.backlogLinkId === linkId || p.repoLinkId === linkId)).length;
        },
        async loadUserSettingsAsync() {
            try {
                const resp = await this.$http.get('/Config/Settings');
                if (resp && resp.data) {
                    const defaultStartLog = new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10);
                    this.currentUserStartLogDate = resp.data.startLogDate || defaultStartLog;
                    this.currentUserDailyCapacity = resp.data.dailyCapacity || 8;
                    this.currentUserDefaultRole = this.normalizeRole(resp.data.employeeRole);
                    this.settingsForm = {
                        dailyCapacity: resp.data.dailyCapacity || 8,
                        startLogDate: resp.data.startLogDate ? resp.data.startLogDate.substring(0, 10) : defaultStartLog,
                        employeeRole: this.normalizeRole(resp.data.employeeRole)
                    };
                }
            } catch (err) {
                console.warn('Could not load user settings:', err);
            }
        },
        async saveUserSettingsAsync() {
            this.savingSettings = true;
            try {
                const formData = new FormData();
                formData.append('DailyCapacity', this.settingsForm.dailyCapacity);
                if (this.settingsForm.startLogDate) {
                    formData.append('StartLogDate', this.settingsForm.startLogDate);
                }
                formData.append('EmployeeRole', this.settingsForm.employeeRole);

                await this.$http.post('/Config/SaveSettings', formData);
                this.toastSuccess('Saved', 'Your capacity preferences have been updated.');
                await this.initSessionConfigAsync();
            } catch (err) {
                this.toastError('Save Failed', 'Could not save user preferences.');
            } finally {
                this.savingSettings = false;
            }
        },
        async loadArchivedCountAsync() {
            try {
                const resp = await this.$http.get('/Config/ArchivedCount');
                if (resp && resp.data && resp.data.count !== undefined) {
                    this.archivedCount = resp.data.count;
                }
            } catch (err) {
                console.warn('Could not load archived count:', err);
            }
        },
        async confirmPurgeArchives() {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Permanently clean up ${this.archivedCount} archived item(s)? This removes them permanently and cannot be recovered.`,
                {
                    title: 'Clean Up Archives',
                    okVariant: 'danger',
                    okTitle: 'Clean Up Permanently',
                    cancelTitle: 'Cancel',
                    centered: true
                }
            );
            if (!confirmed) return;

            try {
                const resp = await this.$http.post('/Config/PurgeArchives');
                const purged = resp.data?.purgedCount ?? this.archivedCount;
                this.toastSuccess('Archives Purged', `${purged} archived item(s) permanently removed.`);
                this.archivedCount = 0;
                await this.loadProjectsAsync();
                await this.loadPlatformLinksAsync();
            } catch (err) {
                this.toastError('Error', 'Failed to purge archives.');
            }
        },

        // Platform Links
        openAddPlatformLinkModal() {
            if (!this.canAddPlatformConnection) {
                this.toastUpgradeRequired('Pro Plan', 'Connecting multiple platforms or organizations requires the Pro Plan.');
                return;
            }
            this.$refs.platformLinkModal.open();
        },
        editPlatformLink(link) {
            this.$refs.platformLinkModal.open(link);
        },
        async onPlatformLinkSaved() {
            this.toastSuccess('Success', 'Platform connection saved successfully.');
            await this.loadPlatformLinksAsync();
            await this.initSessionConfigAsync();
        },
        async verifyPlatformLinkAsync(link) {
            this.verifyingLinkId = link.id;
            try {
                const resp = await this.$http.post(`/Config/VerifyPlatformLink/${link.id}`);
                if (resp.data && resp.data.success) {
                    this.toastSuccess('Verified', resp.data.message || 'Connection verified successfully!');
                    link.isVerified = true;
                    link.lastVerifiedAt = resp.data.verifiedAt || new Date().toISOString();
                } else {
                    this.toastError('Verification Failed', resp.data?.message || 'Could not verify connection.');
                    link.isVerified = false;
                }
            } catch (err) {
                this.toastError('Error', err.response?.data?.message || err.message || 'Failed to verify platform link.');
            } finally {
                this.verifyingLinkId = null;
            }
        },
        async deletePlatformLinkAsync(link) {
            const linkedProjects = this.projectsList.filter(
                p => !p.isArchived && (p.backlogLinkId === link.id || p.repoLinkId === link.id)
            );
            if (linkedProjects.length > 0) {
                const projectNames = linkedProjects
                    .map(p => `"${p.projectName || p.backlogProjectName || p.repoProjectName}"`)
                    .join(', ');
                await this.$bvModal.msgBoxOk(
                    `Cannot delete connection "${link.name}" because it is currently in use by ${linkedProjects.length} active project(s): ${projectNames}.\n\nTo prevent accidental data loss of project time logs and configuration, please reassign or delete these projects first.`,
                    {
                        title: 'Connection In Use',
                        okVariant: 'warning',
                        okTitle: 'Understood',
                        centered: true
                    }
                );
                return;
            }

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Are you sure you want to delete platform connection "${link.name}"?`,
                {
                    title: 'Delete Platform Connection',
                    okVariant: 'danger',
                    okTitle: 'Delete',
                    cancelTitle: 'Cancel',
                    centered: true
                }
            );
            if (!confirmed) return;

            try {
                await this.$http.delete(`/Config/DeletePlatformLink/${link.id}`);
                this.toastSuccess('Deleted', 'Platform connection deleted.');
                await this.loadPlatformLinksAsync();
                await this.loadProjectsAsync();
                await this.initSessionConfigAsync();
            } catch (err) {
                const msg = err.response?.data?.message || err.message || 'Failed to delete platform link.';
                this.toastError('Cannot Delete Connection', msg, 10);
            }
        },

        // Project Spaces
        openAddProjectWizard() {
            if (!this.canAddProjectSpace && !this.canAddProject) {
                this.toastUpgradeRequired('Pro Plan', 'Configuring multiple project spaces requires the Pro Plan.');
                return;
            }
            this.isEditingProject = false;
            this.isEditingMemberPreferences = false;
            this.editingProjectId = null;
            this.resetWizard();
            this.showWizardModal = true;
        },
        onRoleSelectionChange() {
            if (!this.wizardForm.selectedRoles || !this.wizardForm.selectedRoles.includes(0)) {
                this.wizardForm.selectedRoles = [0];
            }
            this.wizardForm.employeeRoleOverride = 0;
        },
        resetWizard() {
            this.wizardStep = this.isEditingMemberPreferences ? 3 : 1;
            this.isEditingProject = false;
            this.isEditingMemberPreferences = false;
            this.editingProjectId = null;
            this.useCustomProjectName = false;
            this.wizardAvailableProjects = [];
            this.wizardAvailableTeams = [];
            this.wizardAvailableRepoProjects = [];
            this.resetWizardFieldMap();
            this.wizardForm = {
                projectName: '',
                dailyCapacity: this.currentUserDailyCapacity || 8,
                employeeRoleOverride: this.normalizeRole(this.currentUserDefaultRole ?? 0),
                selectedRoles: [0],
                isPrimary: this.activeProjectsList.length === 0,
                backlogFieldMap: emptyFieldMap(),
                enableBacklog: true,
                backlogLinkId: this.backlogEligibleLinks[0]?.id || null,
                selectedBacklogProject: null,
                selectedTeam: [],
                enableRepos: true,
                sameAsBacklog: true,
                repoLinkId: null,
                selectedRepoProject: null
            };
            if (this.wizardForm.backlogLinkId) {
                this.loadWizardBacklogProjects(this.wizardForm.backlogLinkId);
            }
        },
        async onWizardEnableBacklogChange(val) {
            if (val) {
                if (!this.wizardForm.backlogLinkId && this.backlogEligibleLinks.length) {
                    this.wizardForm.backlogLinkId = this.backlogEligibleLinks[0].id;
                }
                if (this.wizardForm.backlogLinkId && (!this.wizardAvailableProjects || !this.wizardAvailableProjects.length)) {
                    await this.loadWizardBacklogProjects(this.wizardForm.backlogLinkId);
                }
            }
        },
        async onWizardEnableReposChange(val) {
            if (val) {
                if (!this.wizardForm.repoLinkId && this.repoEligibleLinks.length) {
                    this.wizardForm.repoLinkId = this.repoEligibleLinks[0].id;
                }
                const effectiveLinkId = this.wizardEffectiveRepoLinkId;
                if (effectiveLinkId && !this.wizardForm.sameAsBacklog && (!this.wizardAvailableRepoProjects || !this.wizardAvailableRepoProjects.length)) {
                    await this.onWizardRepoLinkChange(effectiveLinkId);
                }
            }
        },
        async onWizardBacklogLinkChange(linkId) {
            this.wizardForm.selectedBacklogProject = null;
            this.wizardForm.selectedTeam = this.isEditingProject ? null : [];
            if (!this.useCustomProjectName && !this.isEditingProject) {
                this.wizardForm.projectName = '';
            }
            await this.loadWizardBacklogProjects(linkId);
        },
        async loadWizardBacklogProjects(linkId) {
            if (!linkId) return;
            this.wizardLoadingProjects = true;
            try {
                const resp = await this.$http.get(`/Config/PlatformLinkProjects/${linkId}`);
                if (String(this.wizardForm.backlogLinkId) !== String(linkId)) return;
                this.wizardAvailableProjects = resp.data || [];
            } catch (err) {
                console.error('Failed to load projects for link:', err);
                if (String(this.wizardForm.backlogLinkId) !== String(linkId)) return;
                this.wizardAvailableProjects = [];
            } finally {
                if (String(this.wizardForm.backlogLinkId) === String(linkId)) this.wizardLoadingProjects = false;
            }
        },
        goToWizardStep3() {
            if (!this.useCustomProjectName && this.defaultProjectNameSuggestion) {
                this.wizardForm.projectName = this.defaultProjectNameSuggestion;
            }
            this.wizardStep = 3;
        },
        async onWizardBacklogProjectChange(project) {
            this.wizardForm.selectedTeam = this.isEditingProject ? null : [];
            if (project && !this.useCustomProjectName && !this.isEditingProject) {
                this.wizardForm.projectName = this.defaultProjectNameSuggestion || project.name;
            }
            if (!project || !this.wizardForm.backlogLinkId) return;
            this.wizardLoadingTeams = true;
            try {
                const resp = await this.$http.get(`/Config/PlatformLinkTeams/${this.wizardForm.backlogLinkId}/${project.id}`);
                this.wizardAvailableTeams = resp.data || [];
                if (this.wizardAvailableTeams.length === 1) {
                    this.wizardForm.selectedTeam = this.isEditingProject ? this.wizardAvailableTeams[0] : [this.wizardAvailableTeams[0]];
                    if (!this.useCustomProjectName && !this.isEditingProject && this.defaultProjectNameSuggestion) {
                        this.wizardForm.projectName = this.defaultProjectNameSuggestion;
                    }
                }
            } catch (err) {
                console.error('Failed to load teams for project:', err);
                this.wizardAvailableTeams = [];
            } finally {
                this.wizardLoadingTeams = false;
            }
        },
        async onWizardRepoLinkChange(linkId) {
            this.wizardForm.selectedRepoProject = null;
            if (!linkId) return;
            this.wizardLoadingRepoProjects = true;
            try {
                const resp = await this.$http.get(`/Config/PlatformLinkRepoProjects/${linkId}`);
                this.wizardAvailableRepoProjects = resp.data || [];
            } catch (err) {
                console.error('Failed to load repo projects:', err);
                this.wizardAvailableRepoProjects = [];
            } finally {
                this.wizardLoadingRepoProjects = false;
            }
        },
        onWizardRepoProjectChange() {
            if (!this.useCustomProjectName && (!this.wizardForm.enableBacklog || !this.wizardForm.selectedBacklogProject)) {
                if (this.wizardEffectiveRepoProject) {
                    this.wizardForm.projectName = this.wizardEffectiveRepoProject.name;
                }
            }
        },
        onCustomProjectNameToggle(isChecked) {
            if (!isChecked && this.defaultProjectNameSuggestion) {
                this.wizardForm.projectName = this.defaultProjectNameSuggestion;
            }
        },
        resetWizardFieldMap() {
            this.wizardFieldMapOpen = false;
            this.wizardFieldMapLoaded = false;
            this.wizardFieldMapLoading = false;
            this.wizardFieldMapFields = [];
            this.wizardFieldMapResolved = null;
            this.wizardFieldMapError = '';
        },
        async toggleWizardFieldMap() {
            this.wizardFieldMapOpen = !this.wizardFieldMapOpen;
            if (this.wizardFieldMapOpen && !this.wizardFieldMapLoaded) {
                await this.loadWizardFieldMapAsync();
            }
        },
        async loadWizardFieldMapAsync() {
            if (!this.editingProjectId) return;
            this.wizardFieldMapLoading = true;
            this.wizardFieldMapError = '';
            try {
                const resp = await this.$http.get('/TaskManagement/GetFieldMapInfo', { headers: { 'X-Project-Id': this.editingProjectId } });
                this.wizardFieldMapFields = (resp.data && resp.data.fields) || [];
                this.wizardFieldMapResolved = (resp.data && resp.data.resolved) || null;
                this.wizardFieldMapLoaded = true;
            } catch (err) {
                const data = err.response && err.response.data;
                this.wizardFieldMapError = (typeof data === 'string' && data) || (data && data.message) || 'Could not load the fields of this project.';
            } finally {
                this.wizardFieldMapLoading = false;
            }
        },
        fieldMapOptions(slot) {
            const numeric = /^(picklist)?(integer|double|decimal)$/i;
            const fields = (this.wizardFieldMapFields || []).filter(f => {
                if (!f.referenceName || /^System\./i.test(f.referenceName)) return false;
                const type = f.fieldType || '';
                if (slot.kind === 'identity') return f.isIdentity || /identity/i.test(type);
                if (slot.kind === 'number') return numeric.test(type);
                return !f.isIdentity && !numeric.test(type);
            });
            const options = fields.map(f => ({ value: f.referenceName, text: `${f.name} (${f.referenceName})` }));
            const current = this.wizardForm.backlogFieldMap[slot.key];
            if (current && !options.some(o => o.value === current)) {
                options.unshift({ value: current, text: current });
            }
            return [{ value: null, text: 'Auto-detect' }, ...options];
        },
        fieldMapHint(slot) {
            const resolved = this.wizardFieldMapResolved ? this.wizardFieldMapResolved[slot.key] : '';
            return this.wizardFieldMapLoaded ? `Currently used: ${resolved || 'none'}` : '';
        },
        buildFieldMapPayload() {
            const map = this.wizardForm.backlogFieldMap || {};
            return FIELD_MAP_SLOTS.reduce((acc, s) => ({ ...acc, [s.key]: map[s.key] || null }), {});
        },
        async saveWizardProjectAsync() {
            this.wizardSavingProject = true;
            try {
                const bProj = this.wizardForm.selectedBacklogProject;
                const bTeams = this.isEditingProject 
                    ? (this.wizardForm.selectedTeam ? [this.wizardForm.selectedTeam] : []) 
                    : (this.wizardForm.selectedTeam || []);
                const bTeamsToSave = bTeams.length > 0 ? bTeams : [null];

                let repoLinkId = null;
                let repoProjectId = null;
                let repoProjectName = null;

                if (this.wizardForm.enableRepos) {
                    if (this.wizardForm.sameAsBacklog && this.wizardForm.enableBacklog && this.canMirrorBacklogToRepo) {
                        repoLinkId = this.wizardForm.backlogLinkId;
                        repoProjectId = bProj ? bProj.id : null;
                        repoProjectName = bProj ? bProj.name : null;
                    } else if (this.wizardEffectiveRepoLinkId && this.wizardEffectiveRepoProject) {
                        repoLinkId = this.wizardEffectiveRepoLinkId;
                        repoProjectId = this.wizardEffectiveRepoProject.id;
                        repoProjectName = this.wizardEffectiveRepoProject.name;
                    }
                }

                let savedProjectId = null;
                for (let i = 0; i < bTeamsToSave.length; i++) {
                    const bTeam = bTeamsToSave[i];
                    const finalProjectName = (bTeamsToSave.length > 1 && bTeam) 
                        ? `${this.wizardForm.projectName} (${bTeam.name})`
                        : this.wizardForm.projectName;

                    const payload = {
                        ...(this.isEditingProject && this.editingProjectId ? { id: this.editingProjectId } : {}),
                        projectName: finalProjectName,
                        dailyCapacity: this.wizardForm.dailyCapacity,
                        employeeRoleOverride: this.wizardForm.employeeRoleOverride,
                        isPrimary: this.wizardForm.isPrimary,
                        backlogLinkId: this.wizardForm.enableBacklog ? this.wizardForm.backlogLinkId : null,
                        backlogProjectId: this.wizardForm.enableBacklog && bProj ? bProj.id.toString() : '',
                        backlogProjectName: this.wizardForm.enableBacklog && bProj ? bProj.name : '',
                        backlogTeamId: this.wizardForm.enableBacklog && bTeam ? bTeam.id.toString() : null,
                        backlogTeamName: this.wizardForm.enableBacklog && bTeam ? bTeam.name : null,
                        repoLinkId: repoLinkId,
                        repoProjectId: repoProjectId ? repoProjectId.toString() : null,
                        repoProjectName: repoProjectName,
                        backlogFieldMap: (this.isEditingProject && !this.isEditingMemberPreferences) ? this.buildFieldMapPayload() : undefined
                    };

                    const saveResp = await this.$http.post('/Config/SaveProjectSpace', payload);
                    if (saveResp?.data?.id) {
                        savedProjectId = saveResp.data.id;
                    }
                }
                const title = this.isEditingProject ? 'Project Space Updated' : 'Project Space Created';
                const msg = this.isEditingProject
                    ? `Project space "${this.wizardForm.projectName}" updated successfully!`
                    : `Project space "${this.wizardForm.projectName}" configured successfully!`;
                this.toastSuccess(title, msg);
                this.showWizardModal = false;
                await this.loadProjectsAsync();
                await this.initSessionConfigAsync();

                const targetProjId = savedProjectId || (this.isEditingProject ? this.editingProjectId : null);
                this.$store.dispatch('invalidateSprintState', targetProjId);
                if (targetProjId) {
                    this.$store.commit('setSelectedProjectSpaceId', targetProjId);
                    this.$store.commit('setSelectedProjectId', targetProjId);
                    try {
                        localStorage.setItem('velocytrack_last_project_id', targetProjId);
                    } catch (e) {
                        // ignore
                    }
                    eventBus.$emit('project-space-config-changed', targetProjId);
                    eventBus.$emit('global-project-changed', targetProjId);
                }
            } catch (err) {
                const action = this.isEditingProject ? 'update' : 'create';
                const msg = err.response?.data?.message || err.message || `Failed to ${action} project space.`;
                this.toastError(this.isEditingProject ? 'Update Failed' : 'Creation Failed', msg);
            } finally {
                this.wizardSavingProject = false;
            }
        },

        // Edit Project
        async openEditProjectModal(proj, options = {}) {
            this.isEditingProject = true;
            this.isEditingMemberPreferences = !!options.memberPreferencesOnly || proj.canManageSpace === false;
            this.editingProjectId = proj.id;
            this.useCustomProjectName = !!(proj.projectName && proj.backlogProjectName && proj.projectName !== proj.backlogProjectName);
            this.wizardStep = this.isEditingMemberPreferences ? 3 : 1;

            const initialRole = this.normalizeRole(
                proj.employeeRoleOverride !== null && proj.employeeRoleOverride !== undefined
                    ? proj.employeeRoleOverride
                    : (this.currentUserDefaultRole ?? 0)
            );

            const hasBacklog = !!(proj.backlogLinkId && proj.backlogProjectId);
            const hasRepo = !!(proj.repoLinkId && (proj.repoProjectId || proj.repoProjectName));
            const shouldEnableBacklog = hasBacklog || !!options.enableBacklogIfMissing;
            const isSameAsBacklog = hasBacklog && hasRepo && proj.repoLinkId === proj.backlogLinkId &&
                (proj.repoProjectName === proj.backlogProjectName || proj.repoProjectId === proj.backlogProjectId);

            const initialBacklogLinkId = proj.backlogLinkId || (this.backlogEligibleLinks[0]?.id || null);
            const initialRepoLinkId = proj.repoLinkId || (this.repoEligibleLinks[0]?.id || null);

            this.wizardForm = {
                projectName: proj.projectName,
                dailyCapacity: proj.dailyCapacity || 8,
                employeeRoleOverride: initialRole,
                selectedRoles: [0],
                isPrimary: proj.isPrimary,
                backlogFieldMap: { ...emptyFieldMap(), ...(proj.backlogFieldMap || {}) },
                enableBacklog: shouldEnableBacklog,
                backlogLinkId: initialBacklogLinkId,
                selectedBacklogProject: proj.backlogProjectId ? { id: proj.backlogProjectId, name: proj.backlogProjectName } : null,
                selectedTeam: proj.backlogTeamName ? { id: proj.backlogTeamId, name: proj.backlogTeamName } : null,
                enableRepos: hasRepo,
                sameAsBacklog: isSameAsBacklog,
                repoLinkId: initialRepoLinkId,
                selectedRepoProject: (proj.repoProjectId || proj.repoProjectName) ? { id: proj.repoProjectId || proj.repoProjectName, name: proj.repoProjectName || proj.repoProjectId } : null
            };

            this.wizardAvailableProjects = [];
            this.wizardAvailableTeams = [];
            this.wizardAvailableRepoProjects = [];
            this.resetWizardFieldMap();

            this.showWizardModal = true;

            // Load backlog projects & teams
            if (initialBacklogLinkId) {
                await this.loadWizardBacklogProjects(initialBacklogLinkId);
                if (proj.backlogProjectId && this.wizardAvailableProjects.length) {
                    const found = this.wizardAvailableProjects.find(p => p.id === proj.backlogProjectId || p.name === proj.backlogProjectName);
                    if (found) {
                        this.wizardForm.selectedBacklogProject = found;
                    }
                }

                if (proj.backlogLinkId && proj.backlogProjectId) {
                    this.wizardLoadingTeams = true;
                    try {
                        const resp = await this.$http.get(`/Config/PlatformLinkTeams/${proj.backlogLinkId}/${proj.backlogProjectId}`);
                        this.wizardAvailableTeams = resp.data || [];
                        if (proj.backlogTeamId || proj.backlogTeamName) {
                            const foundTeam = this.wizardAvailableTeams.find(t => t.id === proj.backlogTeamId || t.name === proj.backlogTeamName);
                            if (foundTeam) {
                                this.wizardForm.selectedTeam = foundTeam;
                            }
                        }
                    } catch (err) {
                        console.error('Failed to load teams for project in edit mode:', err);
                    } finally {
                        this.wizardLoadingTeams = false;
                    }
                }
            }

            // Load repo projects
            const effectiveRepoLinkId = isSameAsBacklog ? initialBacklogLinkId : initialRepoLinkId;
            if (effectiveRepoLinkId) {
                this.wizardLoadingRepoProjects = true;
                try {
                    const resp = await this.$http.get(`/Config/PlatformLinkRepoProjects/${effectiveRepoLinkId}`);
                    this.wizardAvailableRepoProjects = resp.data || [];
                    if ((proj.repoProjectId || proj.repoProjectName) && this.wizardAvailableRepoProjects.length) {
                        const foundRepo = this.wizardAvailableRepoProjects.find(p => p.id === proj.repoProjectId || p.name === proj.repoProjectName);
                        if (foundRepo) {
                            this.wizardForm.selectedRepoProject = foundRepo;
                        }
                    }
                } catch (err) {
                    console.error('Failed to load repo projects in edit mode:', err);
                } finally {
                    this.wizardLoadingRepoProjects = false;
                }
            }
        },
        async setPrimaryProjectAsync(proj) {
            try {
                await this.$http.post(`/Config/SetPrimaryProjectSpace/${proj.id}`);
                this.toastSuccess('Primary Project Space Set', `"${proj.projectName || proj.projectSpaceName}" is now your primary project space.`);
                await this.loadProjectsAsync();
                await this.initSessionConfigAsync();
            } catch (err) {
                this.toastError('Error', 'Could not set primary project space.');
            }
        },
        async deleteProjectAsync(proj) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Archive project space "${proj.projectName || proj.projectSpaceName}"? It will be removed from your active list and can be cleaned up in Archives.`,
                {
                    title: 'Archive Project Space',
                    okVariant: 'danger',
                    okTitle: 'Archive',
                    cancelTitle: 'Cancel',
                    centered: true
                }
            );
            if (!confirmed) return;

            try {
                await this.$http.delete(`/Config/DeleteProjectSpace/${proj.id}`);
                this.toastSuccess('Archived', `Project space "${proj.projectName || proj.projectSpaceName}" archived.`);
                this.$store.dispatch('invalidateSprintState', proj.id);
                await this.loadProjectsAsync();
                await this.loadArchivedCountAsync();
                await this.initSessionConfigAsync();
                eventBus.$emit('project-space-config-changed', proj.id);
                eventBus.$emit('global-project-changed', null);
            } catch (err) {
                this.toastError('Error', 'Failed to archive project space.');
            }
        },

        // Workspace & Teams Management
        async loadWorkspaceAsync() {
            this.wsLoading = true;
            try {
                if (!this.isDevEnv) {
                    const wsResp = await this.$http.get('/Workspace').catch(() => ({ data: null }));
                    this.wsWorkspace = wsResp.data || null;
                    if (this.wsWorkspace) {
                        this.$store.commit('setWorkspace', this.wsWorkspace);
                    }
                    return;
                }
                const [wsResp, listResp, pendingResp] = await Promise.all([
                    this.$http.get('/Workspace').catch(() => ({ data: null })),
                    this.$http.get('/Workspace/List').catch(() => ({ data: [] })),
                    this.$http.get('/Workspace/Invitations/Pending').catch(() => ({ data: [] }))
                ]);
                this.wsWorkspace = wsResp.data || null;
                if (this.wsWorkspace) {
                    this.$store.commit('setWorkspace', this.wsWorkspace);
                }
                const allWorkspaces = Array.isArray(listResp.data) ? listResp.data : [];
                this.teamsList = allWorkspaces.filter(w => w && w.isTeamSpace !== false);
                this.incomingInvitations = Array.isArray(pendingResp.data) ? pendingResp.data : [];
                this.$store.commit('setWorkspaces', this.teamsList);
                this.$store.commit('setPendingTeamInvitations', this.incomingInvitations);
            } catch (err) {
                console.warn('Could not load teams:', err);
            } finally {
                this.wsLoading = false;
            }
        },
        teamSlotCount(team) {
            if (!team) return 1;
            if (team.seatSlots && team.seatSlots > 0) return team.seatSlots;
            return Math.max(1, Math.ceil((team.seatLimit || 5) / 5));
        },
        isTeamOwner(team) {
            if (!team) return false;
            return team.currentUserRole === 0 || team.currentUserRole === 'Owner';
        },
        isTeamOwnerOrAdmin(team) {
            if (!team) return false;
            return team.currentUserRole === 0 || team.currentUserRole === 1 ||
                   team.currentUserRole === 'Owner' || team.currentUserRole === 'Admin';
        },
        connectSharedSpaceAuth(proj) {
            return this.connectSharedProjectSpaceAuth(proj);
        },
        connectSharedProjectSpaceAuth(proj) {
            const pType = this.normalizePlatformType(proj.requiredBacklogPlatformType ?? proj.backlogPlatformType ?? 0);
            const slug = proj.requiredBacklogTenantSlug || proj.requiredRepoTenantSlug || '';
            this.$refs.platformLinkModal.open({
                platformType: pType,
                tenantSlug: slug,
                name: slug ? `${this.platformTypeName(pType)} (${slug})` : this.platformTypeName(pType),
                supportsBacklog: true,
                supportsRepos: true
            });
        },
        openCreateTeamModal() {
            if (!this.canCreateNewTeamSpace) {
                if (this.isBetaUser) {
                    this.toastInfo(
                        'Beta Batch Limit Reached',
                        'Private Beta accounts include 1 complimentary 5-seat batch (1 Team Space with up to 5 seats). Move to the Production instance (velocytrack.com) to add more batches or Team Spaces.'
                    );
                } else {
                    this.$router.push('/plans?reason=Team+Spaces+%26+5-Seat+Batches&requiredPlan=Team');
                }
                return;
            }
            this.isEditingTeam = false;
            this.editingTeamId = null;
            const defaultSpaceIds = this.personalOrAllProjectsForSharing
                .filter(p => p.isPrimary)
                .map(p => p.id);
            this.teamForm = {
                name: '',
                description: '',
                seatSlots: 1,
                projectSpaceIds: defaultSpaceIds
            };
            this.showCreateTeamModal = true;
        },
        openEditTeamModal(team) {
            this.isEditingTeam = true;
            this.editingTeamId = team.workspaceId;
            const spaces = team.linkedProjectSpaces || team.sharedProjectSpaces || [];
            const linkedIds = spaces
                .map(sp => sp.templateProjectSpaceId || sp.memberProjectSpaceId || sp.id)
                .filter(Boolean);
            // Also match by name if templateProjectSpaceId isn't in user's local list
            const matchedLocalIds = this.personalOrAllProjectsForSharing
                .filter(p => linkedIds.includes(p.id) || spaces.some(sp => sp.projectSpaceName === p.projectName))
                .map(p => p.id);
            this.teamForm = {
                name: team.name || '',
                description: team.description || '',
                seatSlots: this.teamSlotCount(team),
                projectSpaceIds: matchedLocalIds
            };
            this.showCreateTeamModal = true;
        },
        async saveTeamAsync() {
            if (!this.teamForm.name || this.savingTeam) return;
            this.savingTeam = true;
            try {
                if (this.isEditingTeam && this.editingTeamId) {
                    const resp = await this.$http.put(`/Workspace/${this.editingTeamId}`, {
                        name: this.teamForm.name,
                        description: this.teamForm.description,
                        projectSpaceIds: this.teamForm.projectSpaceIds
                    });
                    this.toastSuccess('Team Space Updated', `"${this.teamForm.name}" settings and shared Project Spaces saved.`);
                    if (this.managingTeam && this.managingTeam.workspaceId === this.editingTeamId && resp.data) {
                        this.managingTeam = resp.data;
                    }
                } else {
                    await this.$http.post('/Workspace', {
                        name: this.teamForm.name,
                        description: this.teamForm.description,
                        seatSlots: Math.max(1, this.teamForm.seatSlots || 1),
                        projectSpaceIds: this.teamForm.projectSpaceIds
                    });
                    this.toastSuccess('Team Space Created', `Team Space "${this.teamForm.name}" created with ${Math.max(1, this.teamForm.seatSlots || 1) * 5} seats!`);
                }
                this.showCreateTeamModal = false;
                await this.loadWorkspaceAsync();
                await this.loadProjectsAsync();
                await this.initSessionConfigAsync();
            } catch (err) {
                this.toastError('Save Failed', err.response?.data?.message || 'Could not save Team Space.');
            } finally {
                this.savingTeam = false;
            }
        },
        async openManageTeamModal(team) {
            this.managingTeam = team;
            this.wsInviteEmail = '';
            this.wsInviteRole = 2;
            this.wsInviteCapacity = this.currentUserDailyCapacity || 8;
            this.showManageTeamModal = true;
            await Promise.all([
                this.loadWsMembersAsync(team.workspaceId),
                this.loadTeamOutgoingInvitationsAsync(team.workspaceId)
            ]);
        },
        async loadWsMembersAsync(workspaceId) {
            const targetId = workspaceId || this.managingTeam?.workspaceId || this.wsWorkspace?.workspaceId;
            try {
                const resp = await this.$http.get('/Workspace/Members', {
                    params: targetId ? { workspaceId: targetId } : {}
                });
                this.wsMembers = resp.data || [];
            } catch (err) {
                console.warn('Could not load workspace members:', err);
            }
        },
        async loadTeamOutgoingInvitationsAsync(workspaceId) {
            const targetId = workspaceId || this.managingTeam?.workspaceId;
            if (!targetId) {
                this.managingTeamInvitations = [];
                return;
            }
            try {
                const resp = await this.$http.get(`/Workspace/${targetId}/Invitations`);
                this.managingTeamInvitations = Array.isArray(resp.data) ? resp.data : [];
            } catch (err) {
                this.managingTeamInvitations = [];
            }
        },
        async addFiveSeatSlotAsync(team) {
            if (!team || this.addingSeatSlot) return;
            this.addingSeatSlot = true;
            try {
                const nextSlots = this.teamSlotCount(team) + 1;
                await this.$http.put(`/Workspace/${team.workspaceId}`, {
                    name: team.name,
                    description: team.description,
                    seatSlots: nextSlots
                });
                await this.loadWorkspaceAsync();
                const updated = this.teamsList.find(t => t.workspaceId === team.workspaceId);
                if (updated) {
                    this.managingTeam = updated;
                } else {
                    team.seatSlots = nextSlots;
                    team.seatLimit = nextSlots * 5;
                }
                this.toastSuccess('Seat Batch Allocated', `Allocated +1 batch slot (+5 seats). Total Team Space capacity is now ${nextSlots * 5} seats.`);
            } catch (err) {
                this.toastError('Error', err.response?.data?.message || 'Could not allocate 5-seat batch slot.');
            } finally {
                this.addingSeatSlot = false;
            }
        },
        async wsInviteMemberAsync() {
            if (!this.wsInviteEmail || this.wsInviting) return;
            const targetId = this.managingTeam?.workspaceId || this.wsWorkspace?.workspaceId;
            this.wsInviting = true;
            try {
                await this.$http.post(`/Workspace/${targetId}/Invite`, {
                    workspaceId: targetId,
                    email: this.wsInviteEmail.trim().toLowerCase(),
                    role: this.wsInviteRole,
                    dailyCapacityOverride: this.wsInviteCapacity || 8
                });
                this.toastSuccess('Invitation Sent', `Invited ${this.wsInviteEmail} to ${this.managingTeam?.name || 'the team'}.`);
                this.wsInviteEmail = '';
                this.wsInviteRole = 2;
                await Promise.all([
                    this.loadWsMembersAsync(targetId),
                    this.loadTeamOutgoingInvitationsAsync(targetId),
                    this.loadWorkspaceAsync()
                ]);
                if (this.managingTeam) {
                    const refreshed = this.teamsList.find(t => t.workspaceId === targetId);
                    if (refreshed) this.managingTeam = refreshed;
                }
            } catch (err) {
                this.toastError('Invite Failed', err.response?.data?.message || 'Failed to invite member.');
            } finally {
                this.wsInviting = false;
            }
        },
        async saveMemberSettingsAsync(member) {
            const targetId = this.managingTeam?.workspaceId || this.wsWorkspace?.workspaceId;
            if (!targetId || !member) return;
            try {
                const roleNum = typeof member.role === 'number'
                    ? member.role
                    : ({ Owner: 0, Admin: 1, Member: 2, ReadOnly: 3 }[member.role] ?? 2);
                await this.$http.patch(`/Workspace/${targetId}/Members/${member.accountUid}`, {
                    role: roleNum,
                    dailyCapacityOverride: member.dailyCapacityOverride || 8,
                    externalIdentity: member.externalIdentity || null
                });
                this.toastSuccess('Member Updated', `Updated settings for ${member.fullName || member.email}.`);
            } catch (err) {
                this.toastError('Update Failed', err.response?.data?.message || 'Could not update member settings.');
            }
        },
        async wsRemoveMemberAsync(member) {
            const targetId = this.managingTeam?.workspaceId || this.wsWorkspace?.workspaceId;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Remove ${member.fullName || member.email} from ${this.managingTeam?.name || 'the team'}? Past time logs recorded while active will remain preserved in historical sprint reports.`,
                {
                    title: 'Remove Squad Member',
                    okVariant: 'danger',
                    okTitle: 'Remove Member',
                    cancelTitle: 'Cancel',
                    centered: true
                }
            );
            if (!confirmed) return;

            try {
                await this.$http.delete(`/Workspace/Members/${member.accountUid}`, {
                    params: targetId ? { workspaceId: targetId } : {}
                });
                this.toastSuccess('Member Removed', 'User removed from active squad roster.');
                await Promise.all([
                    this.loadWsMembersAsync(targetId),
                    this.loadWorkspaceAsync()
                ]);
                if (this.managingTeam) {
                    const refreshed = this.teamsList.find(t => t.workspaceId === targetId);
                    if (refreshed) this.managingTeam = refreshed;
                }
            } catch (err) {
                this.toastError('Error', err.response?.data?.message || 'Failed to remove member.');
            }
        },
        buildShareableInviteUrl(token) {
            if (!token) return '';
            const origin = window.location.origin || 'https://beta.velocytrack.dev:44999';
            return `${origin}/config?teamInvite=${token}`;
        },
        async copyShareableInviteLink(token) {
            const url = this.buildShareableInviteUrl(token);
            if (!url) return;
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(url);
                }
                this.toastSuccess('Invite Link Copied', 'Shareable team invitation link copied to clipboard.');
            } catch (e) {
                this.toastInfo('Invite Link', url);
            }
        },
        async toggleInviteLinkAsync(team, enabled) {
            if (!team) return;
            try {
                const resp = await this.$http.post(`/Workspace/${team.workspaceId}/InviteLink/Toggle`, { enabled: !!enabled });
                if (resp.data) {
                    team.inviteLinkEnabled = resp.data.inviteLinkEnabled;
                    team.inviteToken = resp.data.inviteToken;
                }
                this.toastSuccess('Invite Link Updated', enabled ? 'Shareable invite link enabled.' : 'Shareable invite link disabled.');
            } catch (err) {
                this.toastError('Error', 'Could not update invite link status.');
            }
        },
        async regenerateInviteLinkAsync(team) {
            if (!team) return;
            try {
                const resp = await this.$http.post(`/Workspace/${team.workspaceId}/InviteLink/Regenerate`);
                if (resp.data) {
                    team.inviteToken = resp.data.inviteToken;
                    team.inviteLinkEnabled = resp.data.inviteLinkEnabled;
                }
                this.toastSuccess('Link Regenerated', 'Generated a new shareable invite link. Previous link is now invalid.');
            } catch (err) {
                this.toastError('Error', 'Could not regenerate invite link.');
            }
        },
        async revokeInvitationAsync(inv) {
            if (!inv || !this.managingTeam) return;
            try {
                await this.$http.delete(`/Workspace/${this.managingTeam.workspaceId}/Invitations/${inv.invitationId}`);
                this.toastSuccess('Invitation Revoked', `Revoked pending invitation for ${inv.inviteeEmail}.`);
                await Promise.all([
                    this.loadTeamOutgoingInvitationsAsync(this.managingTeam.workspaceId),
                    this.loadWorkspaceAsync()
                ]);
                if (this.managingTeam) {
                    const refreshed = this.teamsList.find(t => t.workspaceId === this.managingTeam.workspaceId);
                    if (refreshed) this.managingTeam = refreshed;
                }
            } catch (err) {
                this.toastError('Error', 'Could not revoke invitation.');
            }
        },
        async acceptTeamInvitationAsync(inv) {
            if (!inv || !inv.token) return;
            await this.acceptTeamInvitationByTokenAsync(inv.token);
        },
        async acceptTeamInvitationByTokenAsync(token) {
            if (!token) return;
            this.respondingInviteToken = token;
            try {
                const resp = await this.$http.post(`/Workspace/Invitations/${token}/Accept`);
                const wsName = resp.data?.name || 'the team';
                this.toastSuccess('Joined Team!', `You have joined ${wsName}. Shared Project Spaces have been added to your workspace.`);
                await Promise.all([
                    this.loadWorkspaceAsync(),
                    this.loadProjectsAsync(),
                    this.initSessionConfigAsync()
                ]);
            } catch (err) {
                this.toastError('Invitation Error', err.response?.data?.message || 'Could not accept team invitation.');
            } finally {
                this.respondingInviteToken = null;
            }
        },
        async declineTeamInvitationAsync(inv) {
            if (!inv || !inv.token) return;
            this.respondingInviteToken = inv.token;
            try {
                await this.$http.post(`/Workspace/Invitations/${inv.token}/Decline`);
                this.toastInfo('Invitation Declined', `Declined invitation to ${inv.workspaceName}.`);
                await this.loadWorkspaceAsync();
            } catch (err) {
                this.toastError('Error', 'Could not decline invitation.');
            } finally {
                this.respondingInviteToken = null;
            }
        },
        async leaveTeamAsync(team) {
            if (!team) return;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Leave team "${team.name}"? Your personal Project Spaces remain untouched, and past hours logged while you were active stay in historical team sprint reports.`,
                {
                    title: 'Leave Team',
                    okVariant: 'danger',
                    okTitle: 'Leave Team',
                    cancelTitle: 'Cancel',
                    centered: true
                }
            );
            if (!confirmed) return;
            try {
                await this.$http.post(`/Workspace/${team.workspaceId}/Leave`);
                this.toastSuccess('Left Team', `You have left "${team.name}".`);
                await Promise.all([
                    this.loadWorkspaceAsync(),
                    this.loadProjectsAsync(),
                    this.initSessionConfigAsync()
                ]);
            } catch (err) {
                this.toastError('Error', err.response?.data?.message || 'Could not leave team.');
            }
        },
        async deleteTeamAsync(team) {
            if (!team) return;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Archive team "${team.name}"?`,
                {
                    title: 'Archive Team',
                    okVariant: 'danger',
                    okTitle: 'Archive Team',
                    cancelTitle: 'Cancel',
                    centered: true
                }
            );
            if (!confirmed) return;
            try {
                await this.$http.delete(`/Workspace/${team.workspaceId}`);
                this.toastSuccess('Team Archived', `"${team.name}" has been archived.`);
                await Promise.all([
                    this.loadWorkspaceAsync(),
                    this.loadProjectsAsync(),
                    this.initSessionConfigAsync()
                ]);
            } catch (err) {
                this.toastError('Error', err.response?.data?.message || 'Could not archive team.');
            }
        },
        openTeamDashboard(team) {
            this.$router.push({
                path: '/team-dashboard',
                query: team && team.workspaceId ? { workspaceId: team.workspaceId } : {}
            });
        },
        wsRoleName(role) {
            if (role === 0 || role === '0' || role === 'Owner') return 'Owner';
            if (role === 1 || role === '1' || role === 'Admin') return 'Admin';
            if (role === 2 || role === '2' || role === 'Member') return 'Member';
            if (role === 3 || role === '3' || role === 'ReadOnly' || role === 'Guest') return 'Read-Only';
            return 'Member';
        },

        // Helpers
        normalizePlatformType(type) {
            if (type === 0 || type === '0' || type === 'AzureDevOps') return 0;
            if (type === 1 || type === '1' || type === 'Jira') return 1;
            if (type === 2 || type === '2' || type === 'GitHub') return 2;
            if (type === 3 || type === '3' || type === 'GitLab') return 3;
            return 0;
        },
        platformIconClass(type) {
            const t = this.normalizePlatformType(type);
            switch (t) {
                case 0: return 'icon-azure';
                case 1: return 'fab fa-jira text-info';
                case 2: return 'fab fa-github text-dark';
                case 3: return 'fab fa-gitlab text-warning';
                default: return 'fas fa-cloud';
            }
        },
        platformIconOnly(type) {
            const t = this.normalizePlatformType(type);
            switch (t) {
                case 0: return 'icon-azure';
                case 1: return 'fab fa-jira';
                case 2: return 'fab fa-github';
                case 3: return 'fab fa-gitlab';
                default: return 'fas fa-cloud';
            }
        },
        platformBadgeVariant(type) {
            const t = this.normalizePlatformType(type);
            switch (t) {
                case 0: return 'primary';
                case 1: return 'info';
                case 2: return 'dark';
                case 3: return 'warning';
                default: return 'primary';
            }
        },
        platformIconName(type) {
            const t = this.normalizePlatformType(type);
            switch (t) {
                case 0: return 'azure-devops';
                case 1: return 'jira';
                case 2: return 'github';
                case 3: return 'gitlab';
                default: return 'cloud';
            }
        },
        platformTypeName(type) {
            const t = this.normalizePlatformType(type);
            switch (t) {
                case 0: return 'Azure DevOps';
                case 1: return 'Jira';
                case 2: return 'GitHub';
                case 3: return 'GitLab';
                default: return 'Platform';
            }
        },
        platformPatSettingsUrl(link) {
            if (!link) return '';
            const t = this.normalizePlatformType(link.platformType);
            const orgSlug = encodeURIComponent((link.tenantSlug || link.azureOrganization || '').trim() || 'your-org');
            switch (t) {
                case 0: return `https://dev.azure.com/${orgSlug}/_usersSettings/tokens`;
                case 1: return 'https://id.atlassian.com/manage-profile/security/api-tokens';
                case 2: return 'https://github.com/settings/tokens';
                case 3: return 'https://gitlab.com/-/user_settings/personal_access_tokens';
                default: return '';
            }
        },
        platformPatSettingsTooltip(link) {
            const provider = this.platformTypeName(link ? link.platformType : 0);
            return `Open ${provider} token settings to create, extend, or revoke PAT tokens`;
        },
        roleName(role) {
            const num = this.normalizeRole(role);
            switch (num) {
                case 0: return 'Developer';
                case 1: return 'QA';
                case 2: return 'System Analyst';
                default: return 'Developer';
            }
        },
        formatDate(iso) {
            if (!iso) return 'Never';
            try {
                return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
            } catch {
                return iso;
            }
        }
    }
};
</script>

<style scoped>
.config-page {
    max-width: 1200px;
    margin: 0 auto;
}

.config-section-card {
    background-color: var(--vt-card-bg, #ffffff) !important;
    border-color: var(--vt-card-border, #dee2e6) !important;
    border-radius: 12px;
    overflow: hidden;
    scroll-margin-top: 80px;
}

.config-bottom-spacer {
    padding-bottom: 65vh !important;
}

.config-section-card .card-header {
    background-color: var(--vt-bg, #f8f9fa) !important;
    border-bottom: 1px solid var(--vt-border, #dee2e6) !important;
}

.section-step-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: rgba(2, 132, 199, 0.12);
    color: var(--vt-primary, #0284c7);
    font-weight: 700;
    font-size: 0.85rem;
}

.config-section-nav .btn {
    font-size: 0.82rem;
    transition: all 0.15s ease-in-out;
    display: inline-flex;
    align-items: center;
}

.config-section-nav .btn:hover {
    transform: translateY(-1px);
}

/* Dedicated, polished Section Counter Badges */
.config-section-nav .section-counter-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.35rem;
    height: 1.35rem;
    padding: 0 0.38rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1;
    margin-left: 0.45rem;
    vertical-align: middle;
    transition: all 0.15s ease-in-out;
}

/* 1. Connections (Info) */
.config-section-nav .btn-outline-info .section-counter-badge {
    background-color: rgba(23, 162, 184, 0.12);
    color: #0e7490;
    border: 1px solid rgba(23, 162, 184, 0.32);
}
[data-theme="dark"] .config-section-nav .btn-outline-info .section-counter-badge {
    background-color: rgba(56, 189, 248, 0.16);
    color: #38bdf8;
    border: 1px solid rgba(56, 189, 248, 0.38);
}
[data-theme="dark"] .config-section-nav .btn-outline-info:hover .section-counter-badge,
[data-theme="dark"] .config-section-nav .btn-outline-info:focus .section-counter-badge,
[data-theme="dark"] .config-section-nav .btn-outline-info:active .section-counter-badge,
[data-theme="dark"] .config-section-nav .btn-outline-info.active .section-counter-badge,
.config-section-nav .btn-outline-info:hover .section-counter-badge,
.config-section-nav .btn-outline-info:focus .section-counter-badge,
.config-section-nav .btn-outline-info:active .section-counter-badge,
.config-section-nav .btn-outline-info.active .section-counter-badge {
    background-color: #ffffff !important;
    color: #0891b2 !important;
    border-color: transparent !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.22);
}

/* 2. Project Spaces (Primary) */
.config-section-nav .btn-outline-primary .section-counter-badge {
    background-color: rgba(2, 132, 199, 0.12);
    color: #0369a1;
    border: 1px solid rgba(2, 132, 199, 0.32);
}
[data-theme="dark"] .config-section-nav .btn-outline-primary .section-counter-badge {
    background-color: rgba(96, 165, 250, 0.16);
    color: #60a5fa;
    border: 1px solid rgba(96, 165, 250, 0.38);
}
[data-theme="dark"] .config-section-nav .btn-outline-primary:hover .section-counter-badge,
[data-theme="dark"] .config-section-nav .btn-outline-primary:focus .section-counter-badge,
[data-theme="dark"] .config-section-nav .btn-outline-primary:active .section-counter-badge,
[data-theme="dark"] .config-section-nav .btn-outline-primary.active .section-counter-badge,
.config-section-nav .btn-outline-primary:hover .section-counter-badge,
.config-section-nav .btn-outline-primary:focus .section-counter-badge,
.config-section-nav .btn-outline-primary:active .section-counter-badge,
.config-section-nav .btn-outline-primary.active .section-counter-badge {
    background-color: #ffffff !important;
    color: #0284c7 !important;
    border-color: transparent !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.22);
}

/* 3. Teams (Success) */
.config-section-nav .btn-outline-success .section-counter-badge {
    background-color: rgba(40, 167, 69, 0.12);
    color: #15803d;
    border: 1px solid rgba(40, 167, 69, 0.32);
}
[data-theme="dark"] .config-section-nav .btn-outline-success .section-counter-badge {
    background-color: rgba(74, 222, 128, 0.16);
    color: #4ade80;
    border: 1px solid rgba(74, 222, 128, 0.38);
}
[data-theme="dark"] .config-section-nav .btn-outline-success:hover .section-counter-badge,
[data-theme="dark"] .config-section-nav .btn-outline-success:focus .section-counter-badge,
[data-theme="dark"] .config-section-nav .btn-outline-success:active .section-counter-badge,
[data-theme="dark"] .config-section-nav .btn-outline-success.active .section-counter-badge,
.config-section-nav .btn-outline-success:hover .section-counter-badge,
.config-section-nav .btn-outline-success:focus .section-counter-badge,
.config-section-nav .btn-outline-success:active .section-counter-badge,
.config-section-nav .btn-outline-success.active .section-counter-badge {
    background-color: #ffffff !important;
    color: #16a34a !important;
    border-color: transparent !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.22);
}

/* Section Card Header Counter Badges */
.config-section-card .card-header h5 .badge-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.45rem;
    height: 1.45rem;
    padding: 0 0.42rem;
    border-radius: 999px;
    font-size: 0.76rem;
    font-weight: 700;
    line-height: 1;
    vertical-align: middle;
}

.config-main-tabs {
    background-color: var(--vt-card-bg, #ffffff) !important;
    border-color: var(--vt-card-border, #dee2e6) !important;
}

.config-main-tabs>>>.card-header {
    background-color: var(--vt-bg, #f8f9fa) !important;
    border-bottom-color: var(--vt-border, #dee2e6) !important;
}

.config-main-tabs>>>.tab-content {
    background-color: var(--vt-card-bg, #ffffff) !important;
    color: var(--vt-text, #212529) !important;
}

.tab-counter-badge {
    font-weight: 700;
    font-size: 0.75rem;
    padding: 0.25em 0.6em;
    border-radius: 10rem;
    transition: all 0.15s ease-in-out;
}

.config-main-tabs>>>.nav-pills .nav-link:not(.active) .tab-counter-badge {
    background-color: rgba(255, 255, 255, 0.12) !important;
    color: var(--vt-muted, #94a3b8) !important;
    border: 1px solid var(--vt-border, rgba(255, 255, 255, 0.1));
}

.config-main-tabs>>>.nav-pills .nav-link.active .tab-counter-badge {
    background-color: rgba(255, 255, 255, 0.28) !important;
    color: #ffffff !important;
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
}

[data-theme="light"] .config-main-tabs>>>.nav-pills .nav-link:not(.active) .tab-counter-badge {
    background-color: #e9ecef !important;
    color: #495057 !important;
    border: 1px solid #ced4da !important;
}

.config-main-tabs>>>.nav-pills .nav-link.active i {
    color: #ffffff !important;
}

[data-theme="dark"] .badge-light {
    background-color: rgba(255, 255, 255, 0.08) !important;
    border-color: var(--vt-border, #2d3748) !important;
    color: var(--vt-muted, #94a3b8) !important;
}

[data-theme="dark"] .alert-secondary,
[data-theme="dark"] .alert-light {
    background-color: #1e293b !important;
    border-color: #334155 !important;
    color: #e2e8f0 !important;
}

[data-theme="dark"] .alert-secondary .text-muted,
[data-theme="dark"] .alert-light .text-muted {
    color: #94a3b8 !important;
}

[data-theme="dark"] .review-card {
    background-color: #17202c !important;
    border-color: #334155 !important;
}

.empty-state-card {
    background-color: rgba(0, 0, 0, 0.02) !important;
    border-color: var(--vt-border, #dee2e6) !important;
}

[data-theme="dark"] .empty-state-card {
    background-color: rgba(255, 255, 255, 0.02) !important;
    border-color: var(--vt-border, #2d3748) !important;
}

[data-theme="dark"] .badge-dark {
    background-color: #334155 !important;
    border: 1px solid var(--vt-border, #475569) !important;
    color: #f8fafc !important;
}

.empty-icon-circle {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: rgba(0, 120, 212, 0.12) !important;
    border: 1px solid rgba(0, 120, 212, 0.25);
    color: #0078d4;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem;
}

.project-card,
.platform-card {
    transition: all 0.2s ease;
    background: var(--vt-card-bg, #ffffff) !important;
    border-color: var(--vt-card-border, #dee2e6) !important;
    color: var(--vt-text, #212529) !important;
}

.project-card:hover,
.platform-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15) !important;
}

.project-card .card-header,
.platform-card .card-header {
    background: transparent !important;
    border-bottom-color: var(--vt-border, #dee2e6) !important;
}

.project-card .card-footer,
.platform-card .card-footer {
    background: rgba(0, 0, 0, 0.02) !important;
    border-top-color: var(--vt-border, #dee2e6) !important;
}

[data-theme="dark"] .project-card .card-footer,
[data-theme="dark"] .platform-card .card-footer {
    background: rgba(255, 255, 255, 0.02) !important;
    border-top-color: var(--vt-border, #2d3748) !important;
}

.source-row {
    background-color: rgba(0, 0, 0, 0.03) !important;
    border-color: var(--vt-border, #dee2e6) !important;
}

[data-theme="dark"] .source-row {
    background-color: rgba(255, 255, 255, 0.04) !important;
    border-color: var(--vt-border, #2d3748) !important;
}

.capacity-badge {
    background-color: rgba(0, 0, 0, 0.05) !important;
    border: 1px solid var(--vt-border, #dee2e6) !important;
    color: var(--vt-muted, #6c757d) !important;
    font-weight: 600;
    padding: 0.35rem 0.55rem;
    font-size: 0.8rem;
    border-radius: 4px;
}

[data-theme="dark"] .capacity-badge {
    background-color: rgba(255, 255, 255, 0.08) !important;
    border: 1px solid var(--vt-border, #2d3748) !important;
    color: #e2e8f0 !important;
}

.is-primary-card {
    border-color: #0078d4 !important;
    border-width: 1.5px !important;
}

.project-avatar-icon,
.platform-type-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
}

.extra-small {
    font-size: 0.75rem;
}

.font-monospace {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.wizard-stepper {
    position: relative;
    padding: 0 16px;
}

.wizard-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 2;
}

.step-icon {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background-color: var(--vt-input-bg, #e9ecef);
    border: 1px solid var(--vt-border, #dee2e6);
    color: var(--vt-muted, #6c757d);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.9rem;
    transition: all 0.2s;
}

.wizard-step.active .step-icon {
    background-color: #0078d4 !important;
    border-color: #0078d4 !important;
    color: #fff !important;
    box-shadow: 0 0 0 3px rgba(0, 120, 212, 0.25);
}

.wizard-step.completed .step-icon {
    background-color: #28a745 !important;
    border-color: #28a745 !important;
    color: #fff !important;
}

.step-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--vt-muted, #6c757d);
    margin-top: 4px;
}

.wizard-step.active .step-label {
    color: #0078d4;
}

.wizard-step-line {
    flex-grow: 1;
    height: 3px;
    background-color: var(--vt-border, #e9ecef);
    margin: 0 8px;
    margin-bottom: 20px;
    transition: all 0.2s;
}

.wizard-step-line.completed {
    background-color: #28a745;
}

.config-panel-overlay {
    min-height: 220px;
    width: 100%;
    display: block;
}


.role-multiselect-box {
    background-color: var(--vt-card-bg, #f8f9fa) !important;
    border-color: var(--vt-border, #dee2e6) !important;
}

[data-theme="dark"] .role-multiselect-box {
    background-color: rgba(255, 255, 255, 0.03) !important;
    border-color: rgba(255, 255, 255, 0.12) !important;
}
[data-theme="dark"] .alert-warning .alert-link {
    color: #fde047 !important;
    text-decoration: underline;
}

.mobile-col-caption {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted, #6c757d);
    margin-bottom: 0.2rem;
}

@media (max-width: 991.98px) {
    .config-section-card .card-header {
        padding: 0.65rem 0.85rem !important;
    }
    .config-section-card .card-body {
        padding: 0.85rem !important;
    }
    .config-section-nav .btn {
        font-size: 0.76rem;
        padding: 0.25rem 0.5rem;
    }
    .config-section-nav .section-counter-badge {
        min-width: 1.25rem;
        height: 1.25rem;
        font-size: 0.68rem;
        margin-left: 0.35rem;
        padding: 0 0.3rem;
    }
    .config-bottom-spacer {
        padding-bottom: 4rem !important;
    }

    /* Roster and Invite Grid Items Card Styling */
    [data-test="table-team-members"] .card,
    [data-test="list-pending-invites"] .card {
        border-radius: 0.65rem !important;
        padding: 0.75rem 0.85rem !important;
        background: rgba(30, 41, 59, 0.65) !important;
        border: 1px solid rgba(255, 255, 255, 0.08) !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18) !important;
        margin-bottom: 0.65rem !important;
    }

    [data-theme="light"] [data-test="table-team-members"] .card,
    [data-theme="light"] [data-test="list-pending-invites"] .card {
        background: #ffffff !important;
        border: 1px solid rgba(0, 0, 0, 0.08) !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
    }

    [data-test="table-team-members"] .card > .card-body,
    [data-test="list-pending-invites"] .card > .card-body {
        padding: 0 !important;
    }

    [data-test="table-team-members"] .grid-row,
    [data-test="list-pending-invites"] .grid-row {
        display: flex !important;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.45rem 0.6rem;
        padding: 0 !important;
        margin: 0 !important;
    }

    [data-test="table-team-members"] .grid-row > [class*="col"],
    [data-test="list-pending-invites"] .grid-row > [class*="col"] {
        flex: 0 1 auto !important;
        width: auto !important;
        max-width: 100% !important;
        padding: 0 !important;
    }

    [data-test="table-team-members"] .grid-col-member {
        order: 1 !important;
        flex: 0 0 100% !important;
        width: 100% !important;
        margin-bottom: 0.2rem !important;
    }

    [data-test="table-team-members"] .grid-col-role,
    [data-test="table-team-members"] .grid-col-capacity {
        order: 2 !important;
    }

    [data-test="table-team-members"] .grid-col-identity {
        order: 3 !important;
        flex: 0 0 100% !important;
        width: 100% !important;
    }

    [data-test="table-team-members"] .grid-col-actions {
        order: 2 !important;
        margin-left: auto !important;
    }

    [data-test="list-pending-invites"] .grid-col-invitee {
        order: 1 !important;
        flex: 0 0 100% !important;
        width: 100% !important;
    }

    [data-test="list-pending-invites"] .grid-col-sent {
        order: 2 !important;
    }

    [data-test="list-pending-invites"] .grid-col-actions {
        order: 2 !important;
        margin-left: auto !important;
    }
}

.config-section-card > .card-header > div:first-child {
    flex: 1 1 260px;
    min-width: 0;
    margin-right: 0.75rem;
}

.config-section-card > .card-header > div:last-child {
    flex-shrink: 0;
    margin-left: auto;
}

[data-theme="dark"] .alert-warning .alert-link:hover {
    color: #ffffff !important;
}
</style>
